// TheBurntPeanut Simulator 9000 - Game Canvas Component
// Mounts HTML5 Canvas, runs game loop, handles input

import { useEffect, useRef, useState, useCallback } from 'react';
// BETA 16.31 — isDevUser import removed when wheel-zoom was disabled. Restore
// `import { isDevUser } from '../game/devUsers';` if dev FOV unlock returns.
// BETA 17.15 — restored for dev users' full-unlock at offer-pool level.
import { isDevUser } from '../game/devUsers';
import { initGame, updateGame, renderGame, handleInteract, registerEnemies, registerItems, registerWeapons, setHudMousePos, getUltraChestRolled, pickUltraChestCard, closeUltraChest, getUltraChestLayout, noteInputSource, type GameState } from '../game/engine';
import { enemyAdapterModule, setCanvasDimensions } from '../game/enemyAdapter';
import { itemsAdapterModule, setLevelUpSelectedIndex, getLevelUpSelectedIndex, handleLevelUpSelection, getLevelUpOptionDisplayData, setLevelUpHoverIndex, getShrineBuffDisplayOptions, getShrineSelectedIndex, setShrineSelectedIndex, setShrineHoverIndex, handleShrineSelection, getChestOptionDisplayData, handleChestItemSelection, getChestHoverIndex, setChestHoverIndex, rerollLevelUpOptions, rerollShrineOptions, rerollChestOptions, skipShrineChoice, skipChestChoice, setBotModeUnlockAll, getItemState } from '../game/itemsAdapter';
import { getSkipRerollButtonRects, computeChoiceCardLayout, beginLevelUpExit, isLevelUpExitActive } from '../game/engine';
import { getRollSeed, setPendingRunSeed } from '../game/rng'; // BETA 20.92 — seeded balance rolls
import { weaponAdapterModule, setWeaponCanvasDimensions, getRunDamageDealt } from '../game/weaponAdapter';
import { getEnemyCount, getEnemySystemState } from '../game/enemyAdapter';
import { getActiveEnemyProjectiles } from '../game/enemySystem';
import { handlePauseClick, handlePauseKeyDown, updatePauseMousePos, setPauseQuitCallback, setPauseSettingsCallback, resetPauseMenu } from '../game/pauseMenu';
import { handleBalancePanelClick, handleBalancePanelKey, isBalancePanelVisible, captureRunSnapshot } from '../game/balancePanel';
import type { CharacterDef } from '../game/assets';
import type { AudioSettings } from '../game/types';
import type { RunStats } from '../pages/Home';
import { initAudio, resumeAudio, startMusic, setMasterVolume, setMusicVolume, setSFXVolume, endMuffledDuck, setBgmContext, getBgmContext, ensureBgmTrackMatchesContext, stopDeathEulogy, getAudioContext, getSfxGainNode } from '../game/audio';
import { initGameSfx } from '../game/gameSfx';
// BETA 15.93 — Death screen click hit-test for REPLAY / QUIT buttons.
import { hitTestDeathScreenButton } from '../game/deathScreen';
// BETA 16.03 — Per Dylan: "you should have added the stuff in supa base
// to track these player runs." Wire finalizeLiveRun(state, 'quit') on
// every player-quit path so quit runs land in Supabase too. Was: only
// natural death/win submitted, leaving zero rows for quit-out runs.
import { finalizeLiveRun, buildLiveRun } from '../game/livePlayTracker';
// BETA 20.67 — last-gasp telemetry flush on tab close (tracking deep-dive).
import { registerUnloadRunProvider } from '../lib/perfTelemetry';
import { startSession, logEvent, updateSnapshot, endSession as endTrackerSession } from '../game/sessionTracker';
import { resetPerformanceTracker, recordFrame, getCurrentFps } from '../game/performanceTracker';
import { getWeaponSystemState } from '../game/weaponAdapter';
import type { GoopSystemState } from '../game/goopSystem';
import { checkProjectedUnlocks } from '../game/goopSystem';
import { ultraChestPool } from '../game/ultraItems';
import { MAPDEV } from '../game/mapDevViewer';   // BETA 23.16 — F9 panic toggle
import DebugPanel from './DebugPanel';
import TouchControls, { isTouchDevice } from './TouchControls';
import { getResolutionScale, recomputeResolutionScale } from '../game/graphicsSettings';
// BETA 16.71 — input-source telemetry (keyboard / touch / gamepad / mouse mix per run)
import { noteInputActivity } from '../game/inputTracker';
import { debugGodMode, debugPlayerSpeedMult, debugTimeScale, debugNavOverlay, setDebugNavOverlay, setDebugTimeScale, setDebugShowWaypoints, setDebugFlowField } from '../game/debugState';
import { toggleBot, isBotEnabled, printBotReport, finalizeRunOnQuit, resetStrategyMemory, getBotDebug, getMapContext } from '../game/autoPlaytester';
import { setBotV2Active, downloadFullCSV, finalizeV2OnQuit } from '../game/autoPlaytester_v2';
import { setLabMode } from '../game/weaponLab';
import { reportLoopCrash } from '../lib/perfTelemetry';   // 23.53 — armored-crash reporting
// BETA 19.34 — Hover + click SFX on in-game choice cards (level-up,
// shrine, chest). The menu chalkboard already played these; the in-game
// card phases were silent.
import { playHover, playClick } from '../game/menuSfx';

// BETA 22.88 — VIRTUAL DESIGN RESOLUTION. The game engine always renders and
// hit-tests in a fixed 1920×1080 space; the canvas transform scales it onto
// the real (16:9-letterboxed) bitmap. One design space at every window size =
// nothing ever reflows, resizes, or drifts (Dylan: "i don't want anything
// ever to move like that").
const VIRTUAL_W = 1920;
const VIRTUAL_H = 1080;

interface GameCanvasProps {
  character: CharacterDef;
  onBack: () => void;
  onOpenSettings?: () => void;
  onRunEnd?: (stats: RunStats) => void;
  audioSettings?: AudioSettings;
  hudScale?: number;
  username?: string;
  goopState?: GoopSystemState;
  /** When true, bot auto-starts immediately and the game loops forever (no menu return) */
  botMode?: boolean;
  /** BETA 13.71: Weapon Visual Test Lab — player frozen + invincible, all items
   *  unlocked, no run-end / no death screen / no bot. Combat systems still tick
   *  so weapons actually fire and play sounds. */
  labMode?: boolean;
}

export default function GameCanvas({ character, onBack, onOpenSettings, onRunEnd, audioSettings, hudScale, username, goopState, botMode, labMode }: GameCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef<GameState | null>(null);
  const rafRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);
  const onBackRef = useRef(onBack);
  const onOpenSettingsRef = useRef(onOpenSettings);
  const onRunEndRef = useRef(onRunEnd);
  const characterRef = useRef(character);
  const hudScaleRef = useRef(hudScale ?? 1.0);
  const usernameRef = useRef(username ?? '');
  const runEndFiredRef = useRef(false);
  // BETA 14.73 — bot-mode death-screen restart watchdog. User reported death screen
  // "sits for like a minute" in production BOT RUN; root cause was a setTimeout(2500ms)
  // that could be lost/throttled. Replaced with wall-clock + frame-based check that
  // fires deterministically every frame the death screen is up.
  const gameOverShownAtRef = useRef(0);          // performance.now() when gameover entered (0 = not pending)
  const gameOverRestartFiredRef = useRef(false); // one-shot per death; reset when 'playing' resumes
  const lastSnapshotTimeRef = useRef(0);   // game-time of last snapshot update
  const lastWaveRef = useRef(0);           // detect floor transitions
  const goopStateRef = useRef(goopState);  // live goopState for mid-run unlock detection
  const knownUnlockIdsRef = useRef<Set<string>>(new Set()); // IDs already shown this run
  const lastUnlockCheckRef = useRef(0);    // game-time of last unlock poll
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [debugOpen, setDebugOpen] = useState(false);
  const [botActive, setBotActive] = useState(!!botMode);
  const botActiveRef = useRef(!!botMode);
  const botModeRef = useRef(!!botMode); // true when launched from pre-game bot toggle

  // BETA 13.47: live FPS readout (always visible, top-right). Pulses red when < 60.
  // Polls performanceTracker.getCurrentFps() every 250ms — cheap re-render.
  const [fps, setFps] = useState(60);
  useEffect(() => {
    const id = setInterval(() => setFps(getCurrentFps()), 250);
    return () => clearInterval(id);
  }, []);

  // BETA 17.31 — Smoke-test hook. Exposes the live ENGINE phase ('menu' /
  // 'playing' / 'paused' / 'levelup' / 'shrine_choice' / 'chest_spin' /
  // 'chest_choice' / 'gameover' / etc.) to scripts/smoke.mjs. Home.tsx's
  // existing __getGamePhase() returns the React SCREEN state ('play' /
  // 'menu' / 'auth' / 'post_run_unlocks'), which doesn't tell us whether
  // the engine actually started ticking. This hook reads stateRef.current
  // directly so the smoke harness can verify the run loop is alive, not
  // just that React mounted the canvas.
  useEffect(() => {
    type W = typeof window & {
      __getEnginePhase?: () => string | null;
      __getPlayer?: () => Record<string, unknown> | null;
      __getBotDebug?: () => Record<string, unknown> | null;
      __getGameState?: () => unknown;
      __setRunSeed?: (n: number | null) => number | null; // BETA 20.92 — force next run's balance seed
      __getRunSeed?: () => number;                          // BETA 20.92 — current run's balance seed
    };
    const w = window as W;
    w.__getEnginePhase = () => stateRef.current?.phase ?? null;
    // BETA 17.42 — Dylan: "my character cant die for some reason." Added
    // __getPlayer so Chrome MCP can read live HP/armor/regen/position/
    // damageBonus etc. to diagnose immortality complaints without
    // having to spelunk through React fibers. Returns a frozen snapshot
    // of stateRef.current.player so caller can't mutate game state.
    w.__getPlayer = () => {
      const p = stateRef.current?.player;
      if (!p) return null;
      // BETA 19.80 — Return the LIVE reference (not a clone) so __startWithBuild
      // can mutate player.level for the start-with-build time-warp feature.
      // (Previously returned `{ ...p }` clone to "prevent caller mutation" —
      // but caller mutation is the entire point of __startWithBuild here.)
      return p as unknown as Record<string, unknown>;
    };
    // BETA 20.48 — return the LIVE full GameState (not a clone) so the Weapon
    // Lab can grant/revoke ultra items via ultraChestPool.collect/remove, which
    // need the whole state (player + enemies + ultraState), not just the player.
    w.__getGameState = () => stateRef.current ?? null;
    // BETA 20.92 — seeded balance rolls. __setRunSeed(n) forces the NEXT run's
    // balance-roll seed (same n → same level-up offers/magnitudes); __getRunSeed()
    // reads the current run's seed (also recorded in the run row). For A/B balance
    // testing: set a seed, change a number, replay the EXACT same offers.
    w.__setRunSeed = (n: number | null) => { setPendingRunSeed(n); return n; };
    w.__getRunSeed = () => getRollSeed();
    // BETA 20.67 — last-gasp telemetry flush (tracking deep-dive HIGH #2):
    // tab close / refresh / crash used to produce ZERO telemetry. On pagehide,
    // snapshot the in-progress run and ship it via sendBeacon (survives
    // unload). Sub-30s aborts are ignored; finished runs (gameover/won) are
    // skipped — their finalize already posted.
    registerUnloadRunProvider(() => {
      const st = stateRef.current;
      if (!st) return null;
      const _ph = st.phase;
      const _inRun = _ph === 'playing' || _ph === 'paused' || _ph === 'levelup' ||
        _ph === 'shrine_choice' || _ph === 'chest_choice' || _ph === 'chest_spin' || _ph === 'ultra_chest';
      if (!_inRun) return null;
      if ((st.time ?? 0) < 30) return null;
      try {
        const run = buildLiveRun(st, 'quit') as Record<string, unknown>;
        run.flushedVia = 'pagehide';
        return run;
      } catch { return null; }
    });
    // BETA 19.80 — Time-warp the active run forward N seconds. Used by
    // __startWithBuild to simulate "this loadout was earned at minute 6"
    // so spawn rate, difficulty multiplier, and the wave-system clock
    // all reflect that late-game pressure. Sets:
    //   state.time     — game-time accumulator (seconds since run start)
    //   state.startTime — wall-clock anchor used by per-tick difficulty calc
    //                     (engine.ts:1478 reads `Date.now() - state.startTime`)
    //   waveState.floorTimeElapsed + floorStartTime — wave spawn curve
    //                     (waveSystem.ts uses these for getSpawnRate)
    // BETA 19.87 — Live weapon system state getter. Replay's weapon level-up
    // loop needs the SAME ws instance the engine uses (dynamic import from
    // outside React gives a fresh instance). This helper returns the live
    // reference so __startWithBuild can call levelUpWeapon(weaponInstance, ...)
    // and have the increment land on the bot's actual weapons.
    (w as { __getWeaponSystemStateLive?: () => unknown }).__getWeaponSystemStateLive = () => {
      return getWeaponSystemState();
    };

    (w as { __setRunTime?: (sec: number) => void }).__setRunTime = (sec) => {
      const s = stateRef.current;
      if (!s || sec <= 0) return;
      s.time = sec;
      // Wall-clock anchor: pretend the run started `sec` seconds ago
      s.startTime = Date.now() - sec * 1000;
      // BETA 19.88 — Cap wave-system clock at 400s (well before the 600s
      // floor-1 final-swarm trigger) so the wave system stays in active
      // spawn mode regardless of how late the original death happened.
      // Otherwise time-warping to 728s (Dylan's LeBron death) instantly
      // tripped `inFinalSwarm = true` → wave system waited for boss summon
      // → bot wiped the 161-enemy onslaught then no more spawns for 40s.
      // Difficulty multiplier from stacked shrines already scales enemy
      // stats — we only need spawn rate to keep flowing.
      const WAVE_CAP = 400;
      const waveSec = Math.min(sec, WAVE_CAP);
      void import('@/game/enemyAdapter').then(em => {
        const ws = (em.enemyAdapterModule as { getWaveState?: () => {
          floorTimeElapsed?: number; totalRunMinutes?: number;
          swarmTriggered?: boolean[]; bossTriggered?: boolean[]; hordeTriggered?: boolean[];
        } | null })?.getWaveState?.();
        if (ws) {
          ws.floorTimeElapsed = waveSec;
          ws.totalRunMinutes = sec / 60;  // keep DIFFICULTY at full time for stat scaling
          // BETA 19.88 — Pre-mark all past trigger gates as TRUE so the wave
          // system doesn't fire all the missed swarms/bosses/hordes at once
          // when its tick catches up. Without this, spawning to t=400 → all
          // 4 swarm-times (60s, 180s, 300s, 420s) trigger simultaneously +
          // any past bosses → 161-enemy onslaught (what Dylan just saw).
          const SWARM_TIMES = [60, 180, 300, 420];
          const BOSS_TIMES = [180, 360];
          const HORDE_TIMES = [240, 480];
          if (Array.isArray(ws.swarmTriggered)) {
            for (let i = 0; i < ws.swarmTriggered.length; i++) {
              if (i < SWARM_TIMES.length && SWARM_TIMES[i] < waveSec) ws.swarmTriggered[i] = true;
            }
          }
          if (Array.isArray(ws.bossTriggered)) {
            for (let i = 0; i < ws.bossTriggered.length; i++) {
              if (i < BOSS_TIMES.length && BOSS_TIMES[i] < waveSec) ws.bossTriggered[i] = true;
            }
          }
          if (Array.isArray(ws.hordeTriggered)) {
            for (let i = 0; i < ws.hordeTriggered.length; i++) {
              if (i < HORDE_TIMES.length && HORDE_TIMES[i] < waveSec) ws.hordeTriggered[i] = true;
            }
          }
        }
      }).catch(() => { /* noop */ });
      console.log('[__setRunTime] warped to', sec, 'sec (wave-clock capped at', waveSec, ')');
    };
    // BETA 17.49 — bot-side debug hook for stuck/stopped-bot diagnosis.
    // Pulls bot.currentGoal + recentMoveDist + various timers from the
    // autoplaytester module. Chrome MCP samples this at high frequency
    // to identify exactly which goal/state is producing zero movement.
    // Direct call to imported getBotDebug — same module instance the
    // game loop uses, so `bot` is live not null.
    // BETA 17.62 — expose shrine-state array for the bot diagnosis layer.
    // Verifies the channel-commit fix in real time (each shrine has a
    // .state field that becomes 'channeling' when the bot stands in
    // SHRINE_ACTIVATION_RADIUS, 'activated' on completion).
    (w as { __getShrineStates?: () => Array<{ id: string; state: string; progress: number; channelMs: number }> }).__getShrineStates = () => {
      const shrines = stateRef.current?.map?.roomMap?.shrines ?? [];
      return shrines.map(s => ({
        id: (s as { id?: string }).id ?? 'unknown',
        state: (s as { state?: string }).state ?? 'idle',
        progress: (s as { progress?: number }).progress ?? 0,
        channelMs: (s as { channelMs?: number }).channelMs ?? 0,
      }));
    };
    // BETA 17.67 — expose live inventory + weapon list for the autonomous
    // balance log. Each bot run, the observation harness can sample which
    // items are currently held + which weapons. Long-run patterns reveal
    // OP items (always picked, always carry runs) vs weak items (never
    // picked or low impact).
    (w as { __getInventorySnapshot?: () => { items: Array<{ id: string; name?: string; stacks: number }>; weapons: Array<{ id: string; level: number }>; } | null }).__getInventorySnapshot = () => {
      try {
        const items = getItemState()?.inventory ?? [];
        const ws = getWeaponSystemState()?.weapons ?? [];
        return {
          items: items.map(s => ({
            id: s.item?.id ?? 'unknown',
            name: (s.item as { name?: string })?.name,
            stacks: (s as { stacks?: number }).stacks ?? 1,
          })),
          weapons: ws.map(w => ({
            id: (w as { definitionId?: string }).definitionId ?? 'unknown',
            level: (w as { level?: number }).level ?? 1,
          })),
        };
      } catch { return null; }
    };
    // BETA 17.82 — orb merging telemetry hook. Returns per-type alive count,
    // merged-orb count (value > base orb scatter), max + sum values. Used by
    // the implementer's live in-browser analysis pass to verify merge logic
    // triggers at 100+ same-type orbs and that value preserves correctly.
    (w as { __getOrbStats?: () => Record<string, unknown> | null }).__getOrbStats = () => {
      const s = stateRef.current;
      if (!s || !s.lootOrbs) return null;
      const orbs = s.lootOrbs.orbs || [];
      let xpAlive = 0, goldAlive = 0, healthAlive = 0;
      let xpMerged = 0, goldMerged = 0;
      let xpSum = 0, goldSum = 0;
      let xpMax = 0, goldMax = 0;
      let oldXpAge3 = 0, oldGoldAge3 = 0;
      for (const o of orbs) {
        if (!o.alive) continue;
        if (o.type === 'xp') {
          xpAlive++;
          xpSum += o.value;
          if (o.value > xpMax) xpMax = o.value;
          if (o.value > 8) xpMerged++;
          if (o.age >= 3) oldXpAge3++;
        } else if (o.type === 'gold') {
          goldAlive++;
          goldSum += o.value;
          if (o.value > goldMax) goldMax = o.value;
          if (o.value > 8) goldMerged++;
          if (o.age >= 3) oldGoldAge3++;
        } else healthAlive++;
      }
      return {
        stateTimeSec: Math.round(s.time ?? 0),
        stateTimeMin: ((s.time ?? 0) / 60).toFixed(2),
        totalOrbsInArray: orbs.length,
        xpAlive, goldAlive, healthAlive,
        xpMerged, goldMerged,
        xpSum: Math.round(xpSum), goldSum: Math.round(goldSum),
        xpMaxValue: Math.round(xpMax * 10) / 10, goldMaxValue: Math.round(goldMax * 10) / 10,
        oldXpAge3, oldGoldAge3,
        magnetTimer: (s.lootOrbs as { magnetTimer?: number }).magnetTimer ?? 0,
      };
    };
    // BETA 17.94 — STRESS TELEMETRY HOOK. Returns a unified snapshot for the
    // live stress-test observability panel. Player stats + enemy aggregates +
    // combat rates + frame perf — what Dylan needs to "see what's happening
    // in real time" during the STRESS preset run.
    (w as { __getStressTelemetry?: () => Record<string, unknown> | null }).__getStressTelemetry = () => {
      try {
        const s = stateRef.current;
        if (!s) return null;
        const p = s.player as unknown as Record<string, number>;
        const enemies = (s.enemies ?? []);
        const sAny = s as unknown as { projectiles?: unknown[]; particles?: unknown[] };
        const projectiles = (sAny.projectiles ?? []) as unknown[];
        const particles = (sAny.particles ?? []) as unknown[];
        const num = (k: string, def = 0) => (typeof p[k] === 'number' && isFinite(p[k]) ? p[k] : def);
        // Enemy aggregates
        let avgHp = 0, sumHp = 0, maxEHp = 0, sumDmg = 0, avgDmg = 0, maxDmg = 0;
        let aliveCount = 0;
        for (const e of enemies) {
          const eHp = (e as { hp?: number }).hp ?? 0;
          const eDmg = (e as { damage?: number }).damage ?? 0;
          if (eHp > 0) {
            aliveCount++;
            sumHp += eHp;
            sumDmg += eDmg;
            if (eHp > maxEHp) maxEHp = eHp;
            if (eDmg > maxDmg) maxDmg = eDmg;
          }
        }
        if (aliveCount > 0) { avgHp = sumHp / aliveCount; avgDmg = sumDmg / aliveCount; }
        // Frame perf
        const fp = (s as { _engineFramePerfThisRun?: { avgFrameTimeMs?: number; worstFrameTimeMs?: number; spikes?: unknown[] } })._engineFramePerfThisRun;
        const hp = num('hp');
        const maxHp = num('maxHp');
        return {
          // Player health truth
          hp: Math.round(hp),
          maxHp: Math.round(maxHp),
          hpPct: maxHp > 0 ? +((hp / maxHp) * 100).toFixed(1) : null,
          level: num('level'),
          xp: num('xp'),
          xpToNext: num('xpToNext'),
          // Bonuses (rounded for readability)
          dmgBonusPct: Math.round(num('damageBonus')),
          atkSpeedPct: Math.round(num('attackSpeedBonus')),
          critChancePct: Math.round(num('critChance')),
          critDmgPct: Math.round(num('critDamage')),
          luck: Math.round(num('luck')),
          evasion: Math.round(num('evasion')),
          damageReduction: Math.round(num('damageReduction')),
          pickupRange: Math.round(num('pickupRange')),
          projCountBonus: num('projectileCountBonus'),
          pierceBonus: +num('projectilePierceBonus').toFixed(1),
          bounceBonus: +num('projectileBounceBonus').toFixed(1),
          xpMult: +num('xpMultiplier', 1).toFixed(2),
          goldMult: +num('goldMultiplier', 1).toFixed(2),
          speed: Math.round(num('speed')),
          lifesteal: Math.round(num('lifesteal')),
          regenPerSec: +num('regenPerSec').toFixed(1),
          // Enemy aggregates
          enemiesAlive: aliveCount,
          enemyAvgHp: Math.round(avgHp),
          enemyMaxHp: Math.round(maxEHp),
          enemyAvgDmg: Math.round(avgDmg),
          enemyMaxDmg: Math.round(maxDmg),
          // World entity counts
          projectilesAlive: projectiles.length,
          particlesAlive: particles.length,
          // Combat counters (per-run cumulative — useful as deltas)
          killCount: s.killCount ?? 0,
          damageTakenThisRun: s.damageTakenThisRun ?? 0,
          // Frame perf (set per build by engineFramePerf accumulator)
          avgFrameTimeMs: fp?.avgFrameTimeMs ? +fp.avgFrameTimeMs.toFixed(2) : null,
          worstFrameTimeMs: fp?.worstFrameTimeMs ? +fp.worstFrameTimeMs.toFixed(2) : null,
          frameSpikeCount: fp?.spikes?.length ?? 0,
          // State context
          phase: s.phase,
          stateTime: Math.round((s.time ?? 0) * 10) / 10,
          enginePhase: (s as { _enginePhase?: string })._enginePhase,
        };
      } catch (e) { return { error: String(e) }; }
    };
    w.__getBotDebug = () => {
      const d = getBotDebug();
      if (!d) return null;
      // BETA 17.59 — merge in live map-context fields so Chrome MCP can see
      // exactly what's near the bot each frame (nearest shrine, chest, barrel,
      // enemy count, horde-ahead). Same module the bot uses, live state.
      const s = stateRef.current;
      if (s) {
        const ctx = getMapContext(s);
        return { ...d, ...ctx } as Record<string, unknown>;
      }
      return d as Record<string, unknown>;
    };
    // BETA 19.44 (live audit) — Dylan: bot needs to be smart as fuck, never
    // get hit. To make adjustments we need to SEE what every enemy is doing
    // around the bot. This getter returns the full live enemy snapshot —
    // type, position, velocity, attack state (throw windup, slam phase,
    // lunge), status (frozen/slowed), and a derived `threat` summary so
    // Chrome MCP can spot the moment a hit is incoming.
    (w as { __getEnemyThreats?: () => unknown }).__getEnemyThreats = () => {
      try {
        const ess = getEnemySystemState();
        const st = stateRef.current;
        if (!ess || !st) return null;
        const px = st.player.x, py = st.player.y;
        const out = ess.enemies
          .filter(e => e.alive)
          .map(e => {
            const dx = e.x - px;
            const dy = e.y - py;
            const dist = Math.hypot(dx, dy);
            return {
              id: e.id,
              type: e.definitionId,
              variant: e.variant,
              x: Math.round(e.x),
              y: Math.round(e.y),
              vx: Math.round(e.velocityX),
              vy: Math.round(e.velocityY),
              hp: Math.round(e.currentHP),
              maxHp: Math.round(e.maxHP),
              dmg: Math.round(e.damage),
              speed: Math.round(e.speed),
              dist: Math.round(dist),
              // BETA 19.44 — attack telemetry: when > 0, enemy is winding up
              // a throw / slam / lunge. Bot can use this to dodge BEFORE
              // the projectile / impact spawns.
              throwWindup: Math.round((e.throwWindupTimer ?? 0) * 100) / 100,
              throwTargetX: e.throwTargetX,
              throwTargetY: e.throwTargetY,
              slamPhase: e.slamPhase,
              slamPhaseTimer: Math.round((e.slamPhaseTimer ?? 0) * 100) / 100,
              eatPhase: e.eatPhase,
              lungeActive: (e.lungeUntil ?? 0) > (st.time ?? 0),
              frozen: (e.frozenUntil ?? 0) > (st.time ?? 0),
              slowed: (e.slowedUntil ?? 0) > (st.time ?? 0),
              // Direction from bot → enemy (radians)
              dirFromBot: Math.atan2(dy, dx),
            };
          })
          .sort((a, b) => a.dist - b.dist);
        // Summary: nearby threat density + crowding
        const in150 = out.filter(e => e.dist < 150).length;
        const in300 = out.filter(e => e.dist < 300).length;
        const in500 = out.filter(e => e.dist < 500).length;
        const winding = out.filter(e => e.throwWindup > 0 || e.slamPhase > 0).length;
        const types: Record<string, number> = {};
        for (const e of out) types[e.type] = (types[e.type] ?? 0) + 1;
        return {
          summary: { total: out.length, in150, in300, in500, winding, types },
          enemies: out,
        };
      } catch (e) { return { error: String(e) }; }
    };
    // BETA 19.44 — Active enemy projectiles (throws, slime puddles, etc.).
    // Bot already has dodge logic via getActiveEnemyProjectiles; this exposes
    // the same data to Chrome MCP for live verification.
    (w as { __getEnemyProjectiles?: () => unknown }).__getEnemyProjectiles = () => {
      try {
        const projs = getActiveEnemyProjectiles();
        const st = stateRef.current;
        if (!st) return null;
        const px = st.player.x, py = st.player.y;
        return projs.map(p => {
          const dx = p.x - px;
          const dy = p.y - py;
          const dist = Math.hypot(dx, dy);
          const speed = Math.hypot(p.vx, p.vy);
          // Predict closest-approach to player along projectile path
          let etaToImpact: number | null = null;
          if (speed > 1) {
            const t = (-dx * p.vx + -dy * p.vy) / (speed * speed);
            if (t > 0 && t < 3) {
              const cx = p.x + p.vx * t;
              const cy = p.y + p.vy * t;
              const closeD = Math.hypot(cx - px, cy - py);
              if (closeD < p.radius + 25) etaToImpact = Math.round(t * 100) / 100;
            }
          }
          return {
            x: Math.round(p.x),
            y: Math.round(p.y),
            vx: Math.round(p.vx),
            vy: Math.round(p.vy),
            radius: p.radius,
            dist: Math.round(dist),
            speed: Math.round(speed),
            etaToImpact,
          };
        }).sort((a, b) => {
          // Sort by ETA-to-impact first (incoming threats top), then distance
          const aEta = a.etaToImpact ?? 99;
          const bEta = b.etaToImpact ?? 99;
          if (aEta !== bEta) return aEta - bEta;
          return a.dist - b.dist;
        });
      } catch (e) { return { error: String(e) }; }
    };
    // BETA 19.44 — Hit log: every damage event on the bot this run, so we
    // can answer "WHY did it get hit?" from chrome-devtools. Polled-diff
    // implementation: each frame compare player.hp to lastSampledHp; if a
    // drop happened, log it with the surrounding context.
    let _lastSampledHp = stateRef.current?.player.hp ?? 100;
    const _hitLog: Array<Record<string, unknown>> = [];
    const _hitTickInterval = setInterval(() => {
      try {
        const st = stateRef.current;
        if (!st || st.phase !== 'playing') { _lastSampledHp = st?.player.hp ?? _lastSampledHp; return; }
        const hp = st.player.hp;
        if (hp < _lastSampledHp - 0.5) {
          const damage = _lastSampledHp - hp;
          const ess = getEnemySystemState();
          // Find the closest alive enemy as the most-likely culprit (rough heuristic).
          let closest: { id: number; type: string; variant: string; dist: number } | null = null;
          if (ess) {
            let bestD = Infinity;
            for (const e of ess.enemies) {
              if (!e.alive) continue;
              const d = Math.hypot(e.x - st.player.x, e.y - st.player.y);
              if (d < bestD) {
                bestD = d;
                closest = { id: e.id, type: e.definitionId, variant: e.variant, dist: Math.round(d) };
              }
            }
          }
          _hitLog.push({
            t: Math.round((st.time ?? 0) * 10) / 10,
            damage: Math.round(damage * 10) / 10,
            hpAfter: Math.round(hp),
            playerPos: { x: Math.round(st.player.x), y: Math.round(st.player.y) },
            playerVel: { vx: Math.round(st.player.vx ?? 0), vy: Math.round(st.player.vy ?? 0) },
            likelyCulprit: closest,
            activeProjectiles: getActiveEnemyProjectiles().length,
          });
          if (_hitLog.length > 200) _hitLog.shift();
        }
        _lastSampledHp = hp;
      } catch { /* swallow */ }
    }, 100);
    (w as { __getHitsThisRun?: () => unknown }).__getHitsThisRun = () => ({
      count: _hitLog.length,
      hits: _hitLog,
    });
    (w as { __resetHitLog?: () => void }).__resetHitLog = () => {
      _hitLog.length = 0;
      _lastSampledHp = stateRef.current?.player.hp ?? 100;
    };
    return () => {
      clearInterval(_hitTickInterval);
      delete w.__getEnginePhase;
      delete w.__getPlayer;
      delete w.__getBotDebug;
      delete (w as { __getEnemyThreats?: unknown }).__getEnemyThreats;
      delete (w as { __getEnemyProjectiles?: unknown }).__getEnemyProjectiles;
      delete (w as { __getHitsThisRun?: unknown }).__getHitsThisRun;
      delete (w as { __resetHitLog?: unknown }).__resetHitLog;
    };
  }, []);

  // BETA 16.56 — Fullscreen toggle. Per Dylan: "if on phone or handheld
  // gaming device they need to be able to be in full screen." Tracked
  // with the standard fullscreenchange event so the icon flips correctly
  // even when the user exits via Esc or the browser's UI.
  const [isFs, setIsFs] = useState(false);
  useEffect(() => {
    const onFsChange = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);
  const toggleFullscreen = () => {
    try {
      if (!document.fullscreenElement) {
        const el = document.documentElement;
        // requestFullscreen returns a promise — swallow rejection (some
        // browsers refuse outside of a user gesture; the try/catch handles it).
        void el.requestFullscreen?.().catch(() => {/* user denied or unsupported */});
      } else {
        void document.exitFullscreen?.().catch(() => {/* */});
      }
    } catch {/* ignore */}
  };
  // F11 hotkey for fullscreen (browser default is also F11 but some OSes
  // intercept; this guarantees the in-game button + key both work).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'F11') {
        e.preventDefault();
        toggleFullscreen();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // BETA 16.57 — Mobile auto-fullscreen.
  // BETA 16.66 — moved up to MobileGuards (Home top-level) so it fires
  // on first touch ANYWHERE in the app, not just after the user has
  // started the game. iPhone Safari rejects requestFullscreen + the
  // orientation lock either way (Apple's call) — PWA install via
  // "Add to Home Screen" is the path there.

  // BETA 16.57 — Touch input bridge. TouchControls calls onKey(key, down)
  // and we mutate state.keys accordingly so the engine's existing
  // movement / interact / Q logic works unchanged. Pause goes through
  // the existing pauseMenu module (synthesize an 'escape' keypress).
  const handleTouchKey = useCallback((key: string, down: boolean) => {
    const state = stateRef.current;
    if (!state) return;
    if (down) {
      state.keys.add(key);
      // BETA 16.71 — touch joystick / button activity tags this frame
      // for input-mix telemetry.
      noteInputActivity('touch');
      // E + Q both consume the press immediately on key-down (matches
      // the keyboard handler above which calls handleInteract on E).
      if (key === 'e') {
        handleInteract(state);
        state.keys.delete('e');
      }
    } else {
      state.keys.delete(key);
    }
  }, []);
  const handleTouchPause = useCallback(() => {
    const state = stateRef.current;
    if (!state) return;
    // Synthesize the same flow the keyboard Esc handler uses.
    state.keys.add('escape');
    handlePauseKeyDown(state, 'escape');
    state.keys.delete('escape');
  }, []);

  onBackRef.current = onBack;
  onOpenSettingsRef.current = onOpenSettings;
  onRunEndRef.current = onRunEnd;
  characterRef.current = character;
  usernameRef.current = username ?? '';
  goopStateRef.current = goopState;

  // Sync hudScale prop → game state whenever it changes
  useEffect(() => {
    hudScaleRef.current = hudScale ?? 1.0;
    if (stateRef.current) stateRef.current.hudScale = hudScale ?? 1.0;
  }, [hudScale]);

  // Reactively apply audio settings whenever the prop changes (covers in-game settings changes)
  useEffect(() => {
    if (!audioSettings) return;
    setMasterVolume(audioSettings.masterVolume ?? 0.7);
    setMusicVolume(audioSettings.musicVolume ?? 0.35);
    setSFXVolume(audioSettings.sfxVolume ?? 0.6);
  }, [audioSettings]);

  // BETA 15.92 — Switch BGM to the in-game playlist (Basement E Minor on
  // repeat) on mount, switch back to the lobby playlist on unmount. Per
  // Dylan: "the songs i gave you are main menu songs, once the game loads
  // in and we are actually in game i need it two switch to game music."
  // setBgmContext is idempotent so this is safe even if BGM hasn't started
  // yet (first user gesture starts it from the right context).
  useEffect(() => {
    void setBgmContext('game');
    // BETA 18.8 — Defensive force-resync. Per Dylan: "what happened to in
    // game music and main menu music being separate??? i am in game and
    // can hear the main menu music." If anything drifts the BGM context
    // back to 'lobby' while GameCanvas is still mounted (mount race,
    // BgmControls widget calling resumeBgm from the wrong context, etc.),
    // poll every 2 sec and force-swap. Cheap no-op when already correct.
    const _bgmGuard = window.setInterval(() => {
      try {
        if (getBgmContext() !== 'game') void setBgmContext('game');
        // BETA 18.15 — Also check the currently-playing track URL against
        // the expected playlist. If _bgmContext is correctly 'game' but
        // somehow a LOBBY_PLAYLIST track is playing, force-restart from
        // GAME_PLAYLIST[0]. Catches the edge case where setBgmContext
        // early-returns because the flag is already correct but the
        // source is stale from before a context switch.
        void ensureBgmTrackMatchesContext();
      } catch { /* no-op */ }
    }, 2000);
    return () => {
      window.clearInterval(_bgmGuard);
      void setBgmContext('lobby');
    };
  }, []);

  // Initialize game
  useEffect(() => {
    let mounted = true;
    let animFrame = 0;

    async function start() {
      try {
        // Initialize audio — must happen inside a user-gesture callback (click to play)
        initAudio();
        resumeAudio(); // 🥜 wake the context so the very first run sound (LET'S RIDE) isn't lost
        // 🥜 BETA 22.40 — eagerly init the game SFX/voice context HERE (on game mount),
        // not lazily in weaponAdapter on first weapon fire. That lazy init runs AFTER the
        // run_start line, so a fresh-from-menu start was silent (it worked on restart only
        // because the context persisted from the prior run). Idempotent.
        try { const _ac = getAudioContext(); const _sx = getSfxGainNode(); if (_ac && _sx) initGameSfx(_ac, _sx); } catch { /* */ }
        if (audioSettings) {
          setMasterVolume(audioSettings.masterVolume ?? 0.7);
          setMusicVolume(audioSettings.musicVolume ?? 0.35);
          setSFXVolume(audioSettings.sfxVolume ?? 0.6);
        }
        // BETA 15.81 — Procedural startMusic() (synth drone) DISABLED here.
        // Dylan-provided BGM tracks now play instead, started in Home.tsx on
        // first user interaction and persisting across all phase changes
        // including the lobby → game transition. The procedural drone is
        // still in audio.ts (callable from SoundInspector etc.) but no
        // longer auto-starts. To swap to a different track in-game, call
        // playBgmTrack('/sounds/<new>.wav').
        // startMusic();

        // Register subsystem adapters before init
        registerEnemies(enemyAdapterModule);
        registerWeapons(weaponAdapterModule);
        registerItems(itemsAdapterModule);

        // Bot mode: unlock ALL percs BEFORE initGame so itemsAdapter.init() sees the flag
        if (botModeRef.current) setBotModeUnlockAll(true);
        // BETA 13.71: Weapon Lab also wants every item available so the user can toggle anything.
        if (labMode) {
          setBotModeUnlockAll(true);
          setLabMode(true);
        }
        // BETA 21.61 — dev HUMAN play now RESPECTS the loadout (unchecking finally
        // sticks). Only true bot autoplay (botModeRef, above) + Lab keep the full-
        // unlock override. Explicitly clear the module flag for every human run so a
        // prior bot run in this session can't leak full-unlock into a dev's game.
        // (Was BETA 17.15: dev users got setBotModeUnlockAll(true), which threw away
        // their unchecks. The modern pool already builds from the loadout, with no
        // lifetimeStats gate, so the override is no longer needed — see Home.tsx
        // activateAllForDevMode which keeps every item ELIGIBLE in the menu.)
        if (!botModeRef.current && !labMode) {
          setBotModeUnlockAll(false);
        }

        const state = await initGame(character);
        if (!mounted) return;
        stateRef.current = state;
        startSession(state, usernameRef.current);
        resetPerformanceTracker();
        lastWaveRef.current = state.wave ?? 1;
        lastUnlockCheckRef.current = 0;
        // Seed known-unlocks with whatever was already unlocked before this run
        knownUnlockIdsRef.current = new Set(goopStateRef.current?.unlockedPercIds ?? []);
        setLoading(false);

        // Auto-enable bot if launched in bot mode.
        // BETA 13.42 fix: initGame always calls initBot({ enabled: false }), but
        // dev tooling (e.g. preview eval, brain reimport flows) can pre-enable v1
        // before this init runs. Calling toggleBot() unconditionally then flips
        // the bot OFF — the player just stands still ("wiggling").
        // Make the call idempotent: only toggle if currently disabled.
        if (botModeRef.current) {
          const enabled = isBotEnabled() ? true : toggleBot();
          setBotActive(enabled);
          botActiveRef.current = enabled;
          setBotV2Active(enabled); // sync data-collector bot
          // BETA 13.63: honor persisted goopzone_time_scale instead of hard-coding 8.
          {
            const persisted = (() => { try { const v = parseFloat(localStorage.getItem('goopzone_time_scale') || '1'); return Number.isFinite(v) && v > 0 ? v : 1; } catch { return 8; } })();
            setDebugTimeScale(persisted);
          }
          // BETA 13.64: enable ALL 4 nav overlays on bot start (per user spec)
          setDebugNavOverlay(true);     // room walls + corner triangles
          setDebugShowWaypoints(true);  // enemy waypoint arrows
          setDebugFlowField(true);      // flow field overlay
        }

        // Wire up pause menu callbacks
        setPauseQuitCallback(() => {
          // Save session data before quitting (captures quit outcome)
          const st = stateRef.current;
          if (st) {
            endTrackerSession(st);
            // Save bot run data even if bot didn't die (mid-run quit)
            if (botActiveRef.current) {
              finalizeRunOnQuit(st);
              finalizeV2OnQuit(); // auto-save + auto-download v2 CSV
            }
            // BETA 16.03 — Live-play telemetry on pause-menu QUIT. Per
            // Dylan: "you should have added the stuff in supa base to
            // track these player runs. dev peanut is who i am signed is
            // as and i jst did a play through". Was: only natural death
            // submitted. Quitting mid-run produced zero Supabase rows.
            // Now: pause-quit submits with deathCause='quit'. Bot mode
            // skipped (bot has its own finalizeRun pipeline).
            if (!botActiveRef.current) {
              try { finalizeLiveRun(st, 'quit'); } catch { /* */ }
            }
          }
          // BETA 20.56 — A manual pause-menu QUIT must ALWAYS return to the menu,
          // even in bot mode. The old guard skipped onBack in bot mode (it was
          // meant for the death auto-restart loop), which left a human stuck:
          // on the ROG Ally X, pause worked but QUIT TO MENU did nothing. Stop
          // the bot so it doesn't keep looping, then navigate to the menu.
          if (botActiveRef.current || botModeRef.current) {
            botActiveRef.current = false;
            botModeRef.current = false;
            try { if (isBotEnabled()) toggleBot(); } catch { /* */ }
          }
          onBackRef.current();
        });
        setPauseSettingsCallback(() => {
          onOpenSettingsRef.current?.();
        });

        // BETA 22.88 — Dylan's font spec: wait for the local card fonts
        // (SorryBossTitle/SorryBossBody) before the first canvas draw so no
        // frame ever renders in a fallback font. Race with a 1.5s timeout —
        // a missing font file must NEVER hang the game.
        try {
          await Promise.race([
            Promise.all([
              document.fonts.load('400 32px SorryBossTitle'),
              document.fonts.load('600 24px SorryBossBody'),
              (document.fonts.ready as Promise<unknown>),
            ]),
            new Promise(res => setTimeout(res, 1500)),
          ]);
        } catch { /* fonts optional — fallbacks handle it */ }

        // Start game loop
        let lastTime = performance.now();
        let lastFrameErrLog = 0;   // 23.52 — frame-armor error throttle

        const gameLoop = (time: number) => {
          if (!mounted) return;
          const canvas = canvasRef.current;
          const st = stateRef.current;
          if (!canvas || !st) {
            animFrame = requestAnimationFrame(gameLoop);
            return;
          }

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            animFrame = requestAnimationFrame(gameLoop);
            return;
          }

          // Resize canvas to fill window.
          // BETA 16.59 — Wave 4: resolution scale. Reads
          // graphicsSettings.getResolutionScale() so 75% = render at 75%
          // of screen pixels (cheaper) and CSS scales the canvas back up
          // to fill the viewport. CSS sizing is already handled by the
          // canvas's `style={{width:'100vw',height:'100vh'}}` on the
          // JSX element — the browser will auto-scale the bitmap up to
          // fill the styled rect, so we only mutate canvas.width /
          // canvas.height (the bitmap dims) here.
          // BETA 16.64 — recompute the scale from the current preset on
          // every frame so resize / fullscreen swaps re-target. Cheap.
          // BETA 22.88 — enforced 16:9: the bitmap targets the letterboxed
          // stage (largest 16:9 rect in the window), matching the canvas CSS.
          const w = Math.min(window.innerWidth, window.innerHeight * 16 / 9);
          const h = Math.min(window.innerHeight, window.innerWidth * 9 / 16);
          recomputeResolutionScale();
          const resScale = getResolutionScale();
          // BETA 17.29 — PSBOT cycle-30ap Candidate #2 (Dylan: "visuals
          // blurry"). Multiply by DPR for retina sharpness; cap at 1.5
          // so we don't kill FPS on 3× DPR phones.
          //
          // BETA 17.33 — Dylan tested 2K (2560×1440) live via Chrome MCP
          // and FPS dropped to 12. Root cause: at high viewports the
          // resScale (capped at 1.0) × DPR 1.5 produces multi-megapixel
          // bitmaps. 2560×1440 × 1 × 1.5 = 3840×2160 = 8.3M pixels per
          // frame. Canvas2D can't push that at 60fps on most hardware.
          // Fix: hard pixel-count cap. 1080p × 1.5 DPR = 2880×1620 ≈
          // 4.67M pixels — that's the budget. Anything bigger downscales
          // proportionally; browser CSS-scales the bitmap up to fill the
          // viewport (slight blur on 4K screens but FPS recovers).
          const dpr = Math.min(window.devicePixelRatio ?? 1, 1.5);
          let targetW = Math.max(1, Math.round(w * resScale * dpr));
          const PIXEL_BUDGET = 1920 * 1080 * 1.5 * 1.5; // ~4.67M (1080p × 1.5 DPR)
          {
            const pixels = targetW * (targetW * 9 / 16);
            if (pixels > PIXEL_BUDGET) targetW = Math.floor(targetW * Math.sqrt(PIXEL_BUDGET / pixels));
          }
          // BETA 22.88 — bitmap height derives from width so the pair is an
          // EXACT 16:9 match for the virtual 1920×1080 design space.
          const targetH = Math.max(1, Math.round(targetW * VIRTUAL_H / VIRTUAL_W));
          if (canvas.width !== targetW || canvas.height !== targetH) {
            canvas.width = targetW;
            canvas.height = targetH;
            // game systems think in DESIGN space, not bitmap pixels
            setCanvasDimensions(VIRTUAL_W, VIRTUAL_H);
            setWeaponCanvasDimensions(VIRTUAL_W, VIRTUAL_H);
          }

          // Delta time (capped at 100ms to avoid huge jumps, then scaled by dev time scale)
          // BETA 19.75 — also pass the UNCAPPED real dt to recordFrame so
          // worstFrameMs telemetry shows the actual spike magnitude (was
          // pinned at 100ms by the clamp — hid real stutter from PSBOT
          // perf cycles). The game-logic dt stays clamped for sim stability.
          const realDt = (time - lastTime) / 1000;
          const dt = Math.min(realDt, 0.1) * debugTimeScale;
          lastTime = time;
          // BETA 20.46 — expose the REAL full-frame interval (update + render +
          // browser present) to the engine recorder so its trace/spikes measure
          // ACTUAL fps, not just engine update-compute. Guard the seed/huge first
          // frame + any tab-backgrounded jump (>1s).
          (st as unknown as { _realFrameMs?: number })._realFrameMs = (realDt > 0 && realDt < 1) ? realDt * 1000 : 0;

          // Record frame for performance tracking (FPS + entity load)
          recordFrame(dt, {
            enemies:     getEnemyCount(),  // O(1) — tracked incrementally
            projectiles: getWeaponSystemState()?.projectiles.length ?? 0,
            particles:   st.particles?.length ?? 0,
            lootOrbs:    st.lootOrbs?.orbs.length ?? 0,
            phase:       st.phase,
          }, realDt);

          // Sync viewport dimensions — weapon system uses these for on-screen targeting range
          st.viewportW = w;
          st.viewportH = h;

          // Sync hudScale from settings (in case effect fired before state was ready)
          st.hudScale = hudScaleRef.current;

          // ── Debug: god mode + speed multiplier ──
          if (st.phase === 'playing') {
            if (debugGodMode && st.player.hp < st.player.maxHp) {
              st.player.hp = st.player.maxHp;
            }
            if (debugPlayerSpeedMult !== 1.0) {
              // Clamp to a safe max — base speed is ~120
              st.player.speed = Math.min(120 * debugPlayerSpeedMult, 1200);
            }
          }

          // ── BETA 14.19: Lab takes damage + respawns ──
          // BETA 14.05 — REMOVED the keys.delete() freeze per user request:
          // "i want to be able to walk around".
          // BETA 14.17 — REMOVED the forced invincibleTimer + HP top-up per
          // user "i should be able to take damage from enemies... track
          // deaths and how long i lasted, what i died from etc". Player now
          // takes damage normally; engine.ts intercepts player.hp <= 0 in
          // lab mode and respawns at full HP + 1.5s invuln (logs the death
          // event for WeaponLab CSV). Build / session / CSV row are NOT
          // reset — only HP is restored.
          // BETA 14.19 — REMOVED the 9999-invuln gameover trap that was
          // here as a fallback. User reported "im not taking any damage in
          // weapons testing... still". Root cause: if phase EVER became
          // 'gameover' in lab mode (edge case where engine's lab respawn
          // didn't intercept — e.g. hp went to 0 during 'levelup' phase),
          // this trap set invincibleTimer = 9999 (~2.7 HOURS of invuln),
          // permanently bricking the lab session. Now we just bounce phase
          // back to 'playing' with a normal respawn (no permanent invuln).
          if (labMode && st.phase === 'gameover') {
            st.player.hp = st.player.maxHp;
            st.player.invincibleTimer = 1.5;
            st.phase = 'playing';
          }

          // Reset run-end flag when a new run starts
          if (st.phase === 'playing') {
            runEndFiredRef.current = false;
            // BETA 14.73 — reset death-screen watchdog so the next death can re-arm it.
            gameOverShownAtRef.current = 0;
            gameOverRestartFiredRef.current = false;
          }

          // ── Session telemetry ──
          // Update snapshot every 5 seconds
          if (st.phase === 'playing' && st.time - lastSnapshotTimeRef.current >= 5) {
            lastSnapshotTimeRef.current = st.time;
            updateSnapshot(st);
          }
          // Log floor transitions
          const curWave = st.wave ?? 1;
          if (curWave !== lastWaveRef.current) {
            lastWaveRef.current = curWave;
            logEvent('floor_enter', { floor: curWave });
            updateSnapshot(st); // fresh snapshot on every floor
          }

          // ── Mid-run unlock detection (poll every 2 s during play) ──
          if (st.phase === 'playing' && goopStateRef.current &&
              st.time - lastUnlockCheckRef.current >= 2) {
            lastUnlockCheckRef.current = st.time;

            // Build projected lifetime stats: baseline + what's accumulated this run
            const base = goopStateRef.current.lifetimeStats;
            const projected = {
              ...base,
              totalKills:            base.totalKills + (st.killCount ?? 0),
              longestSurvivalMinutes: Math.max(base.longestSurvivalMinutes, st.time / 60),
              highestWaveReached:    Math.max(base.highestWaveReached, st.wave ?? 1),
              highestLevelReached:   Math.max(base.highestLevelReached, st.player.level),
              totalGoldCollected:    base.totalGoldCollected + (st.player.gold ?? 0),
              totalChestsOpened:     base.totalChestsOpened + (st.chestsOpenedThisRun ?? 0),
            };

            const newUnlocks = checkProjectedUnlocks(goopStateRef.current, projected);
            for (const u of newUnlocks) {
              if (knownUnlockIdsRef.current.has(u.id)) continue; // already showed this
              knownUnlockIdsRef.current.add(u.id);

              const notif = { label: u.label, type: u.type as 'perc', timer: 5.0, slideProgress: 0 };
              st.runUnlockNotifs = [...(st.runUnlockNotifs ?? []), notif];
              st.runUnlocksHistory = [...(st.runUnlocksHistory ?? []), { label: u.label, type: u.type as 'perc' }];
            }
          }

          // Update & render.
          // BETA 22.88 — VIRTUAL 1080p DESIGN RESOLUTION (Dylan: "i don't want
          // anything ever to move like that"). The engine ALWAYS renders at
          // 1920×1080 design space; the transform scales it to the real
          // bitmap. Every px-floor clamp (Math.max(16, …)) now sees the same
          // h=1080 at every window size → identical layout at any scale.
          // 23.52 — FRAME ARMOR: one bad frame must NEVER kill the run. An
          // uncaught throw here used to skip the requestAnimationFrame call
          // at the bottom of gameLoop → loop permanently dead → black screen
          // until refresh (Dylan's far-zoom report; root cause was a negative
          // modulo in the canopy bake, fixed in mapChunks — this is the
          // insurance so the NEXT bug of this class only drops one frame).
          try {
            updateGame(st, dt);
            const vScale = canvas.width / VIRTUAL_W;
            ctx.setTransform(vScale, 0, 0, vScale, 0, 0);
            renderGame(ctx, st, VIRTUAL_W, VIRTUAL_H);
          } catch (frameErr) {
            if (time - lastFrameErrLog > 2000) {
              lastFrameErrLog = time;
              console.error('[gameLoop] frame crashed (loop kept alive):', frameErr);
              // 23.53 — armored crashes never reach window.onerror; self-report
              // (red banner + Supabase bug_reports) so Dylan SEES it and the
              // stack is queryable next session.
              reportLoopCrash(frameErr, 'gameLoop-armor');
            }
            // a mid-render throw can leave unbalanced save()/clip state —
            // reset() clears transform + save stack so next frame starts clean
            const cr = ctx as CanvasRenderingContext2D & { reset?: () => void };
            if (typeof cr.reset === 'function') cr.reset();
          }
          ctx.setTransform(1, 0, 0, 1, 0, 0);

          // Fire onRunEnd once when the run ends (gameover or won)
          // BETA 13.71: lab mode never ends a run.
          // BETA 15.94 — In NORMAL mode, onRunEnd is now deferred until the
          // player clicks QUIT on the death screen. Per Dylan: "death screen
          // did not happened. it just put me back in the home screen." Root
          // cause: onRunEnd unmounts GameCanvas (Home.tsx setScreen('menu' |
          // 'post_run_unlocks')), so the new RIP-PEANUT death screen never
          // got to render — the canvas was destroyed mid-frame. Now in normal
          // mode, the gameover transition just captures the snapshot +
          // ends the tracker session; navigation away from GameCanvas only
          // happens when the player explicitly clicks REPLAY (inline restart)
          // or QUIT (calls onRunEnd then onBack — see onMouseDown handler).
          // BOT MODE keeps the auto-fire — bot has no human to click buttons.
          if (!labMode && (st.phase === 'gameover' || st.phase === 'won') && !runEndFiredRef.current) {
            runEndFiredRef.current = true;
            // BETA 14.73 — wall-clock timestamp for the frame-based restart watchdog
            // below. User reported death screen sitting "for like a minute" in BOT RUN.
            // Root cause: the previous setTimeout(2500ms) could be lost/throttled in
            // production. Now we capture wall-clock at gameover entry and check elapsed
            // every frame — deterministic regardless of browser throttling.
            gameOverShownAtRef.current = performance.now();
            gameOverRestartFiredRef.current = false;
            captureRunSnapshot(st);
            endTrackerSession(st);

            if (botActiveRef.current) {
              // Bot mode: fire onRunEnd immediately so the bot watchdog +
              // Home routing flow stays unchanged (bot has no human to
              // click REPLAY/QUIT). Bot mode restart is handled by the
              // watchdog below.
              const elapsed = (Date.now() - st.startTime) / 1000;
              onRunEndRef.current?.({
                kills:        st.killCount,
                floor:        st.wave ?? 1,
                timeSurvived: elapsed,
                characterId:  st.character?.id ?? 'unknown',
                level:        st.player.level ?? 1,
                chestsOpened: st.chestsOpenedThisRun ?? 0,
                damageDealt:  getRunDamageDealt(),
              });
            }
            // Normal mode: onRunEnd fires from QUIT button click handler in
            // onMouseDown so the death screen has time to render the eulogy.
          }

          // BETA 14.73 — bot-mode death-screen restart WATCHDOG. Runs every frame while
          // gameover is showing. Fires exactly once, ~1500 ms after the death screen
          // appeared (wall-clock, so debugTimeScale doesn't dilate it). Replaces the
          // previous setTimeout(2500ms) which the user reported as "sits for like a minute"
          // in production — likely a lost/throttled timer. Frame-based timing guarantees
          // delivery as long as requestAnimationFrame keeps running.
          if (
            !labMode &&
            botActiveRef.current &&
            (st.phase === 'gameover' || st.phase === 'won') &&
            runEndFiredRef.current &&
            !gameOverRestartFiredRef.current &&
            gameOverShownAtRef.current > 0
          ) {
            const elapsedMs = performance.now() - gameOverShownAtRef.current;
            if (elapsedMs >= 1500) {
              gameOverRestartFiredRef.current = true;
              if (botModeRef.current) setBotModeUnlockAll(true); // before initGame
              initGame(characterRef.current).then(newState => {
                if (!botActiveRef.current) return; // user toggled bot off mid-restart
                stateRef.current = newState;
                // initGame always resets the bot to disabled — re-enable it
                const enabled = toggleBot();
                setBotV2Active(enabled); // keep data-collector in sync
              });
            }
          }

          animFrame = requestAnimationFrame(gameLoop);
        }

        animFrame = requestAnimationFrame(gameLoop);
      } catch (err) {
        console.error('[GOOP] Init error:', err);
        if (!mounted) return;
        setLoadError(err instanceof Error ? err.message : 'Failed to load game');
        setLoading(false);
      }
    }

    start();

    return () => {
      mounted = false;
      if (animFrame) cancelAnimationFrame(animFrame);
      // BETA 13.71: clear lab flag on unmount so a normal run after returning to menu doesn't keep it on.
      if (labMode) setLabMode(false);
    };
  }, [character, labMode]);

  // Keyboard input
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      resumeAudio();
      const state = stateRef.current;
      if (!state) return;

      const key = e.key.toLowerCase();
      state.keys.add(key);
      // BETA 16.71 — input-source telemetry. Keys that affect gameplay
      // (movement, interact, active item, reroll) tag this frame as
      // keyboard-active so the input mix builder sees keyboard usage.
      noteInputActivity('keyboard');

      // BETA 23.16 — F9 = MAP-DEV PANIC TOGGLE (Dylan: "when i play i cant
      // walk... its a floating camera im controlling. i need a hotkey to
      // turn controls back on"). Map-dev mode freezes gameplay and gives
      // WASD to a free camera; if it's ever on when you want to PLAY, F9
      // flips it off instantly (and back on for inspection).
      if (e.key === 'F9') {
        e.preventDefault();
        if (!e.repeat) {
          MAPDEV.on = !MAPDEV.on;
          try { console.info(`[mapdev] ${MAPDEV.on ? 'ON — free camera, gameplay frozen' : 'OFF — controls back to Greg'}`); } catch { /* noop */ }
        }
        return;
      }

      // BETA 20.77 — Dylan rebound the pair: "space bar should be the map
      // pull up. and tab should allow me to hide stats, and items so i can
      // see my screen. at any time i can hit tab and they come back."
      // TAB = HUD toggle (chat + left stats + right items panel — same flag
      // the controller Y button flips). Toggle, not hold; ignore key-repeat
      // so holding Tab doesn't strobe. preventDefault keeps browser focus.
      if (e.key === 'Tab') {
        e.preventDefault();
        if (!e.repeat && state.phase === 'playing') state.hudHidden = !state.hudHidden;
      }
      // SPACE (hold) = big centered map — ONLY during play.
      // BETA 20.83 — gate on phase === 'playing'. Dylan hit Space on a
      // level-up screen to peek the map and it CLOSED the screen without
      // letting him pick: Space was ALSO a card-confirm key on the pick
      // screens (19.24). The map is now play-only, and Space was removed
      // from the pick-screen confirm handlers below — Enter / 1-2-3 / click /
      // controller A still confirm. (preventDefault stops the page from
      // scrolling on the Space press.)
      // BETA 21.89 — Dylan: "i can't hit space for the map when a chest / level-up /
      // shrine is open." Space stopped confirming cards back in 20.83, so it's safe to
      // peek the map during those pick screens now too. Allow it in play + all pick phases.
      if (e.key === ' ' && (state.phase === 'playing' || state.phase === 'levelup' ||
          state.phase === 'shrine_choice' || state.phase === 'chest_choice' ||
          state.phase === 'chest_spin' || state.phase === 'ultra_chest')) {
        e.preventDefault();
        state._bigMapKeyHeld = true;
      }

      // BETA 21.89 — Dylan: "i can't pause when a chest / level-up / shrine is open."
      // The pick-screen key handlers below swallow EVERY key (they end in an unconditional
      // return), so escape has to be intercepted HERE first. Remember the screen so resume
      // returns to it (pauseMenu's _resumeToPhase restores _prePausePhase) — no skipped pick.
      if (key === 'escape' && (state.phase === 'levelup' || state.phase === 'shrine_choice' ||
          state.phase === 'chest_choice' || state.phase === 'chest_spin' || state.phase === 'ultra_chest')) {
        e.preventDefault();
        state._prePausePhase = state.phase;
        state.phase = 'paused';
        state.keys.delete('w'); state.keys.delete('a');
        state.keys.delete('s'); state.keys.delete('d');
        state.keys.delete('arrowup'); state.keys.delete('arrowdown');
        state.keys.delete('arrowleft'); state.keys.delete('arrowright');
        resetPauseMenu();
        import('../game/audio').then(a => a.startMuffledDuck(0.35));
        return;
      }

      // E = interact — call immediately and clear key so game loop doesn't double-fire for bot
      if (key === 'e') {
        handleInteract(state);
        state.keys.delete('e'); // consumed here; bot path goes through updateGame instead
      }

      // BETA 19.20 — Stale Q-tray doc removed. The Q-key path (Q = active
      // item activation, VooGoo Rager heal) was deprecated in 16.99
      // alongside the active-item tray. There has been no keyboard
      // handler for 'q' here since then; VooGoo Rager auto-triggers
      // when HP < 50% via the engine apply path. Pause menu still uses
      // Q-to-quit but that handler lives in PauseMenu.tsx.

      // F3 = debug toggle
      if (key === 'f3') {
        e.preventDefault();
        state.debug = !state.debug;
      }

      // ` (backtick) = dev console
      if (key === '`') {
        e.preventDefault();
        setDebugOpen(v => !v);
      }

      // F = quick toggle for the polygon collision debug overlay (BETA 13.23)
      if (key === 'f') {
        e.preventDefault();
        setDebugNavOverlay(!debugNavOverlay);
      }

      // F9 = toggle auto-playtester bot
      if (e.key === 'F9') {
        e.preventDefault();
        const enabled = toggleBot();
        setBotActive(enabled);
        botActiveRef.current = enabled;
        setBotV2Active(enabled); // keep data-collector in sync
        // BETA 13.63: honor persisted goopzone_time_scale on bot enable
        if (enabled) {
          const persisted = (() => { try { const v = parseFloat(localStorage.getItem('goopzone_time_scale') || '1'); return Number.isFinite(v) && v > 0 ? v : 1; } catch { return 8; } })();
          setDebugTimeScale(persisted);
        } else {
          setDebugTimeScale(1);
        }
        // BETA 13.64: enable all 4 nav overlays with bot, disable with bot
        setDebugNavOverlay(enabled);
        setDebugShowWaypoints(enabled);
        setDebugFlowField(enabled);
        console.log(`[BOT] Auto-playtester ${enabled ? 'ENABLED' : 'DISABLED'}`);
        return;
      }

      // F10 = print bot report to console
      if (e.key === 'F10') {
        e.preventDefault();
        printBotReport();
        return;
      }

      // F8 = download 9-sheet CSV from v2 data collector.
      // BETA 16.56 — moved off F11 (now used for fullscreen toggle, since
      // Dylan asked for handheld/mobile fullscreen support — F11 is the
      // universal browser convention).
      if (e.key === 'F8') {
        e.preventDefault();
        downloadFullCSV();
        return;
      }

      // F12 = reset bot strategy memory (wipes per-item correlation data)
      if (e.key === 'F12') {
        e.preventDefault();
        resetStrategyMemory();
        console.log('[BOT] F12: Strategy memory wiped — starting fresh correlation tracking');
        return;
      }

      // M = minimap toggle
      if (key === 'm') {
        state.showMinimap = !state.showMinimap;
      }

      // Zoom is locked — no +/- zoom allowed

      // R = restart (on win screen or game-over screen)
      if (key === 'r' && (state.phase === 'won' || state.phase === 'gameover')) {
        initGame(characterRef.current).then(newState => {
          stateRef.current = newState;
        });
      }

      // Level-up screen input: 1/2/3 to select, Enter to confirm,
      // BETA 18.9: ARROW KEYS to navigate the focus outline (mirrors
      // gamepad — works EVEN IF gamepad is broken on the device).
      // BETA 19.22: Enter prefers activateFocused() so arrow-key
      // navigation actually confirms the FOCUSED card (was using stale
      // legacy selected-index which only 1/2/3/click updated). Falls
      // back to the legacy selected-index path when nothing is focused.
      if (state.phase === 'levelup') {
        const options = getLevelUpOptionDisplayData();
        if (key === '1' && options.length >= 1) setLevelUpSelectedIndex(0);
        if (key === '2' && options.length >= 2) setLevelUpSelectedIndex(1);
        if (key === '3' && options.length >= 3) setLevelUpSelectedIndex(2);
        // BETA 18.9 — Arrow keys cycle the focus outline left/right.
        // Enter activates the currently-focused target.
        if (key === 'arrowleft')  { void import('../game/gamepadFocus').then(m => m.advanceFocus('left')); }
        if (key === 'arrowright') { void import('../game/gamepadFocus').then(m => m.advanceFocus('right')); }
        if (key === 'arrowup')    { void import('../game/gamepadFocus').then(m => m.advanceFocus('up')); }
        if (key === 'arrowdown')  { void import('../game/gamepadFocus').then(m => m.advanceFocus('down')); }
        // BETA 19.24 — Space accepted as alt-confirm alongside Enter.
        // BETA 20.83 — Space REMOVED here: it's the map key now (20.77), and
        // confirming on Space closed the level-up before the player could
        // pick. Enter still confirms; so do 1-2-3, click, and controller A.
        if (key === 'enter') {
          // BETA 19.22 — Try the focus path first so arrow-key nav
          // actually confirms what's outlined. activateFocused returns
          // true when a focused target was fired; if false, fall back
          // to the legacy selected-index handler (1/2/3 + Enter).
          void import('../game/gamepadFocus').then(m => {
            const fired = m.activateFocused();
            if (!fired) {
              const idx = getLevelUpSelectedIndex();
              if (idx >= 0 && idx < options.length && !isLevelUpExitActive()) {
                const chosen = options[idx];
                const success = handleLevelUpSelection(idx);
                if (success) {
                  // BETA 22.87 — exit beat instead of instant resume: chosen
                  // card pulses, losers fade, then engine flips to 'playing'
                  // and flies the card into the PERCS strip.
                  beginLevelUpExit(state, idx, options);
                  logEvent('level_up', { choice: chosen.percName, rarity: chosen.rarity, playerLevel: state.player.level });
                }
              }
            }
          });
        }
        e.preventDefault();
        return;
      }

      // Shrine choice screen input: 1/2/3 to pick directly, Enter to confirm selection
      // BETA 18.10 — arrow keys advance the focus outline (mirror gamepad
      // input via the centralized advanceFocus path).
      // BETA 19.22 — Enter now fires the focused card's onActivate. Was
      // missing entirely on this screen, so keyboard users who arrow-
      // navigated had no way to confirm without falling back to 1/2/3.
      // BETA 19.24 — Space accepted as alt-confirm alongside Enter.
      if (state.phase === 'shrine_choice') {
        if (key === 'arrowleft')  { void import('../game/gamepadFocus').then(m => m.advanceFocus('left')); e.preventDefault(); return; }
        if (key === 'arrowright') { void import('../game/gamepadFocus').then(m => m.advanceFocus('right')); e.preventDefault(); return; }
        if (key === 'arrowup')    { void import('../game/gamepadFocus').then(m => m.advanceFocus('up')); e.preventDefault(); return; }
        if (key === 'arrowdown')  { void import('../game/gamepadFocus').then(m => m.advanceFocus('down')); e.preventDefault(); return; }
        if (key === 'enter') { // BETA 20.83 — Space removed (it's the map key now)
          void import('../game/gamepadFocus').then(m => m.activateFocused());
          e.preventDefault();
          return;
        }
        const options = getShrineBuffDisplayOptions();
        if (key === '1' && options.length >= 1) {
          setShrineSelectedIndex(0);
          const result = handleShrineSelection(0);
          if (result) {
            state.phase = 'playing';
            if (!state.pickupNotifications) state.pickupNotifications = [];
            state.pickupNotifications.push({ text: `${result.percName}`, color: '#00ffcc', timer: 2.5 });
            logEvent('shrine_activated', { perk: result.percName, slot: 1 });
          }
        }
        if (key === '2' && options.length >= 2) {
          setShrineSelectedIndex(1);
          const result = handleShrineSelection(1);
          if (result) {
            state.phase = 'playing';
            if (!state.pickupNotifications) state.pickupNotifications = [];
            state.pickupNotifications.push({ text: `${result.percName}`, color: '#00ffcc', timer: 2.5 });
            logEvent('shrine_activated', { perk: result.percName, slot: 2 });
          }
        }
        if (key === '3' && options.length >= 3) {
          setShrineSelectedIndex(2);
          const result = handleShrineSelection(2);
          if (result) {
            state.phase = 'playing';
            if (!state.pickupNotifications) state.pickupNotifications = [];
            state.pickupNotifications.push({ text: `${result.percName}`, color: '#00ffcc', timer: 2.5 });
            logEvent('shrine_activated', { perk: result.percName, slot: 3 });
          }
        }
        e.preventDefault();
        return;
      }

      // Ultra chest screen: 1 or 2 to pick item.
      // BETA 20.64 — pick logic moved to engine.pickUltraChestCard so keyboard,
      // mouse, AND gamepad (new focus targets) all share one code path (the
      // engine also fires the tracker event + cleans up the focus screen).
      if (state.phase === 'ultra_chest') {
        if (key === '1') pickUltraChestCard(state, 0);
        if (key === '2') pickUltraChestCard(state, 1);
        if (key === 'e') {
          if (getUltraChestRolled().length === 0) closeUltraChest(state);
        }
        e.preventDefault();
        return;
      }

      // Chest choice screen input: 1/2/3 to pick
      // BETA 18.10 — arrow keys advance the focus outline.
      // BETA 19.22 — Enter now fires the focused card's onActivate.
      // Was missing entirely on this screen — same fix as shrine_choice.
      // BETA 19.24 — Space accepted as alt-confirm alongside Enter.
      if (state.phase === 'chest_choice') {
        if (key === 'arrowleft')  { void import('../game/gamepadFocus').then(m => m.advanceFocus('left')); e.preventDefault(); return; }
        if (key === 'arrowright') { void import('../game/gamepadFocus').then(m => m.advanceFocus('right')); e.preventDefault(); return; }
        if (key === 'arrowup')    { void import('../game/gamepadFocus').then(m => m.advanceFocus('up')); e.preventDefault(); return; }
        if (key === 'arrowdown')  { void import('../game/gamepadFocus').then(m => m.advanceFocus('down')); e.preventDefault(); return; }
        if (key === 'enter') { // BETA 20.83 — Space removed (it's the map key now)
          void import('../game/gamepadFocus').then(m => m.activateFocused());
          e.preventDefault();
          return;
        }
        const items = getChestOptionDisplayData();
        const tryPick = (idx: number) => {
          if (items.length > idx) {
            const result = handleChestItemSelection(idx);
            if (result) {
              state.phase = 'playing';
              if (!state.pickupNotifications) state.pickupNotifications = [];
              state.pickupNotifications.push({ text: `ITEM: ${result.itemName}`, color: '#ffd700', timer: 3.0 });
              logEvent('chest_item', { item: result.itemName });
            }
          }
        };
        if (key === '1') tryPick(0);
        if (key === '2') tryPick(1);
        if (key === '3') tryPick(2);
        e.preventDefault();
        return;
      }

      // Escape = toggle pause (or resume if paused)
      if (key === 'escape') {
        e.preventDefault();
        if (state.phase === 'playing') {
          state.phase = 'paused';
          // BETA 18.10 — Dylan: "a wired stick glitch. charcter is now just
          // running left after i got out of pause menu." Stuck-key bug:
          // movement keys held at pause entry were still in state.keys when
          // pause resumed → player auto-moved in that direction with no
          // user input. Release ALL movement keys on pause entry. Same on
          // pause exit (browser may have missed the keyup during the modal).
          state.keys.delete('w'); state.keys.delete('a');
          state.keys.delete('s'); state.keys.delete('d');
          state.keys.delete('arrowup'); state.keys.delete('arrowdown');
          state.keys.delete('arrowleft'); state.keys.delete('arrowright');
          resetPauseMenu();
          // BETA 16.99 — Per Dylan: "music should sound like i have a
          // blanket over the speakers when i hit pause in game."
          // Heavy muffle (depth 0.35 = 35% volume + 700Hz lowpass)
          // makes BGM sound underwater/blanketed during pause.
          // endMuffledDuck() restores on resume below.
          import('../game/audio').then(a => a.startMuffledDuck(0.35));
        } else if (state.phase === 'paused') {
          // Balance panel intercepts ESC to close itself first
          if (isBalancePanelVisible()) { handleBalancePanelKey(key); return; }
          // Delegate to pause menu handler (handles resume + other keys)
          handlePauseKeyDown(state, key);
          // BETA 18.10 — Also release movement keys on resume (mirror of
          // the pause-entry release above). Browser may have missed
          // keyup events while the pause modal was up.
          state.keys.delete('w'); state.keys.delete('a');
          state.keys.delete('s'); state.keys.delete('d');
          state.keys.delete('arrowup'); state.keys.delete('arrowdown');
          state.keys.delete('arrowleft'); state.keys.delete('arrowright');
        } else if (state.phase === 'gameover' || state.phase === 'won') {
          // On gameover/won screens, ESC goes back to menu.
          // BETA 15.94 — Also fire onRunEnd here (deferred from the main
          // game loop) so Home processes lifetime stats / unlocks. Mirrors
          // the QUIT button click in onMouseDown. Both paths unmount
          // GameCanvas immediately so double-fire isn't a concern.
          stopDeathEulogy();
          // BETA 16.03 — Telemetry: submit a 'quit' run to Supabase so
          // PSBOT RUN sees the play even if the player didn't die.
          try { finalizeLiveRun(state, state.phase === 'won' ? 'won' : 'quit'); } catch { /* */ }
          if (state.startTime > 0) {
            const elapsed = (Date.now() - state.startTime) / 1000;
            onRunEndRef.current?.({
              kills:        state.killCount,
              floor:        state.wave ?? 1,
              timeSurvived: elapsed,
              characterId:  state.character?.id ?? 'unknown',
              level:        state.player.level ?? 1,
              chestsOpened: state.chestsOpenedThisRun ?? 0,
              damageDealt:  getRunDamageDealt(),
            });
          }
          onBackRef.current();
        }
        // Overlay phases (levelup / shrine / chest) are intercepted EARLIER (the pick
        // handlers above swallow every key), so they never reach here.
        return; // Don't add escape to keys set
      }

      // If paused, delegate all keys — balance panel intercepts first
      if (state.phase === 'paused') {
        if (isBalancePanelVisible()) { handleBalancePanelKey(key); return; }
        handlePauseKeyDown(state, key);
        return;
      }

      // Prevent default for game keys
      if (['w', 'a', 's', 'd', 'e', ' '].includes(key) || key === 'f3') {
        e.preventDefault();
      }
    }

    function onKeyUp(e: KeyboardEvent) {
      const state = stateRef.current;
      if (!state) return;
      state.keys.delete(e.key.toLowerCase());
      // BETA 20.77 — release SPACE = big map closes, minimap returns.
      // (Tab is a toggle now — no keyup handling needed for it.)
      if (e.key === ' ') {
        state._bigMapKeyHeld = false;
      }
      if (e.key === 'Tab') e.preventDefault();
    }

    // BETA 20.74 — alt-tabbing away mid-hold never delivers the Tab keyup,
    // which would leave the big map stuck open. Clear the hold on blur.
    function onWindowBlur() {
      const state = stateRef.current;
      if (state) state._bigMapKeyHeld = false;
    }
    window.addEventListener('blur', onWindowBlur);

    function canvasMouse(e: MouseEvent): { mx: number; my: number } | null {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      // BETA 22.88 — map into the VIRTUAL 1920×1080 design space (all layout
      // + hit-test math lives there now, not in bitmap pixels)
      const scaleX = VIRTUAL_W / rect.width;
      const scaleY = VIRTUAL_H / rect.height;
      return {
        mx: (e.clientX - rect.left) * scaleX,
        my: (e.clientY - rect.top) * scaleY,
      };
    }

    // BETA 15.80 — Per Dylan: "reroll and skip buttons are not working." Root
    // cause: the modal layouts in engine.ts were migrated to new dimensions
    // (chest cards 340×420 → 300×280, all cards moved from 0.22h → 0.29h)
    // but the hit-test math here was never updated. Buttons were drawn 50+
    // pixels below the click zones, so clicks always missed.
    //
    // cardHitIndex now takes per-phase dimensions instead of hardcoded values.
    // The layouts MUST match engine.ts:
    //   - renderLevelUpScreen:    cardW=340 cardH=460 cardY=Math.min(h*0.29, h-cardH-120)
    //   - renderShrineChoiceScreen: cardW=300 cardH=280 cardY=Math.min(h*0.29, h-cardH-120)
    //   - renderChestChoiceScreen:  cardW=340 cardH=460 cardY=Math.min(h*0.29, h-cardH-120)
    // BETA 16.45 — Card height bumped 420 → 460 across pick screens.
    function cardHitIndex(
      mx: number, my: number, n: number, canvasW: number, canvasH: number,
      cardW: number = 340, cardH: number = 460, gap: number = 24,
    ): number {
      const totalW = n * cardW + (n - 1) * gap;
      const startX = (canvasW - totalW) / 2;
      const cardY = Math.min(canvasH * 0.29, canvasH - cardH - 120);
      for (let i = 0; i < n; i++) {
        const cx = startX + i * (cardW + gap);
        if (mx >= cx && mx <= cx + cardW && my >= cardY && my <= cardY + cardH) return i;
      }
      return -1;
    }
    /** BETA 15.80 — Compute the SAME btnY the render code uses, so skip/reroll
     *  hit zones line up with the drawn buttons. Render does:
     *    btnY = cardY + cardH + 64 (or 58 for chest spin — but that's not
     *    chest_choice, that's chest_spin which doesn't have clickable buttons). */
    function getModalBtnY(canvasH: number, cardH: number): number {
      const cardY = Math.min(canvasH * 0.29, canvasH - cardH - 120);
      return cardY + cardH + 64;
    }

    // BETA 19.34 — Last hover index for the hover-SFX edge detector.
    // Closure-scoped so it persists across mousemove events but resets
    // naturally when this useEffect re-runs (game canvas re-mount).
    const _hoverSfxLastIdxRef = { current: -1 };

    function onMouseMove(e: MouseEvent) {
      const state = stateRef.current;
      if (!state) return;
      // BETA 19.68 — PSBOT cycle-30ba Bug #2: tag mouse as the active input
      // source so the focus-box render in engine.ts only fires when last
      // input was gamepad. Prevents the white box from being stuck on the
      // first card during PC/mouse play.
      noteInputSource('mouse');
      // BETA 17.30 — PSBOT cycle-30aq (Dylan: "i cant click anything").
      // 17.29 multiplied canvas bitmap by DPR (1.5x) for retina sharpness
      // but didn't update click coord conversion. e.clientX/Y comes in
      // CSS pixels (unchanged); canvas.width is now 1.5x bigger. All
      // hit-test math that compares clientX vs canvas-derived button
      // positions misfired by 1.5x. Fix: route every mouse-pos consumer
      // through canvasMouse(e) which already does the canvas.width/
      // rect.width scaling.
      const pos = canvasMouse(e);
      const mx = pos ? pos.mx : e.clientX;
      const my = pos ? pos.my : e.clientY;
      setHudMousePos(mx, my);
      if (state.phase === 'paused') updatePauseMousePos(mx, my);

      // BETA 16.86 — Hover hit-tests use the same computeChoiceCardLayout
      // helper as the render functions (HUD-aware centering, responsive
      // cardW). Was: hardcoded 340×460/300×280 + cardY math drifted off
      // the actual rendered cards on narrow screens with HUD visible.
      const hitInLayout = (mx: number, my: number, n: number, L: ReturnType<typeof computeChoiceCardLayout>): number => {
        for (let k = 0; k < n; k++) {
          const cx = L.startX + k * (L.cardW + L.gap);
          if (mx >= cx && mx <= cx + L.cardW && my >= L.cardY && my <= L.cardY + L.cardH) return k;
        }
        return -1;
      };

      // BETA 19.34 — Hover SFX: fire playHover() ONLY when the hover index
      // transitions from one card to another (or from no card → card).
      // Tracked per-screen on a closure-scoped ref so cards on different
      // screens don't false-trigger when the player moves cursor across
      // phases. Don't play when hover goes to -1 (cursor leaves cards).
      if (state.phase === 'levelup') {
        const pos = canvasMouse(e);
        if (!pos) return;
        const canvas = canvasRef.current!;
        const options = getLevelUpOptionDisplayData();
        if (options.length === 0) { setLevelUpHoverIndex(-1); return; }
        const L = computeChoiceCardLayout(VIRTUAL_W, VIRTUAL_H, options.length, !!state.hudHidden, 'levelup');
        const newIdx = hitInLayout(pos.mx, pos.my, options.length, L);
        if (newIdx >= 0 && newIdx !== _hoverSfxLastIdxRef.current) playHover();
        _hoverSfxLastIdxRef.current = newIdx;
        setLevelUpHoverIndex(newIdx);
      }

      if (state.phase === 'shrine_choice') {
        const pos = canvasMouse(e);
        if (!pos) return;
        const canvas = canvasRef.current!;
        const options = getShrineBuffDisplayOptions();
        const n = options.length;
        if (n === 0) { setShrineHoverIndex(-1); return; }
        const L = computeChoiceCardLayout(VIRTUAL_W, VIRTUAL_H, n, !!state.hudHidden, 'shrine');
        const newIdx = hitInLayout(pos.mx, pos.my, n, L);
        if (newIdx >= 0 && newIdx !== _hoverSfxLastIdxRef.current) playHover();
        _hoverSfxLastIdxRef.current = newIdx;
        setShrineHoverIndex(newIdx);
      }

      if (state.phase === 'chest_choice') {
        const pos = canvasMouse(e);
        if (!pos) return;
        const canvas = canvasRef.current!;
        const items = getChestOptionDisplayData();
        if (items.length === 0) { setChestHoverIndex(-1); return; }
        const L = computeChoiceCardLayout(VIRTUAL_W, VIRTUAL_H, items.length, !!state.hudHidden, 'chest');
        const newIdx = hitInLayout(pos.mx, pos.my, items.length, L);
        if (newIdx >= 0 && newIdx !== _hoverSfxLastIdxRef.current) playHover();
        _hoverSfxLastIdxRef.current = newIdx;
        setChestHoverIndex(newIdx);
      }
    }

    function onMouseDown(e: MouseEvent) {
      resumeAudio();
      const state = stateRef.current;
      const canvas = canvasRef.current;
      if (!state || !canvas) return;

      // BETA 21.17 — Dylan: "DASH WITH RIGHT CLICK." During play, the right mouse
      // button (button 2) feeds the same 'dash' token the engine reads (alongside
      // Shift / right-bumper). The engine does its own rising-edge latch; onMouseUp
      // clears the token so each press = one dash. (contextmenu is already
      // suppressed on the canvas since BETA 21.11, so no menu pops.)
      if (e.button === 2) {
        if (state.phase === 'playing') state.keys.add('dash');
        return; // right-click never falls through to the left-click hit-tests
      }

      // BETA 15.93 — Death screen REPLAY / QUIT click handling. Buttons
      // appear at 3 seconds and stay clickable through the whole eulogy.
      // Per Dylan: "there should be a rel=play button or a quit button
      // that pops up not coverin the text on screen somwhere in the middle
      // bottom after 3 seconds of dying they can press during the entire
      // eulegy."
      if (state.phase === 'gameover') {
        const elapsedSec = state.gameOverStartTime > 0
          ? (Date.now() - state.gameOverStartTime) / 1000
          : 0;
        // BETA 17.30 — canvasMouse converts CSS to bitmap coords (DPR-aware).
        const mPos = canvasMouse(e);
        const mX = mPos ? mPos.mx : e.clientX;
        const mY = mPos ? mPos.my : e.clientY;
        const hit = hitTestDeathScreenButton(mX, mY, VIRTUAL_W, VIRTUAL_H, elapsedSec);
        if (hit === 'replay') {
          // Restart with the same character — no Home navigation, no
          // onRunEnd fired (skips PostRunUnlockScreen for unfinished runs).
          stopDeathEulogy();
          initGame(characterRef.current).then(newState => {
            stateRef.current = newState;
          });
          return;
        }
        if (hit === 'quit') {
          // BETA 15.94 — Fire onRunEnd here (deferred from the main game
          // loop in 15.94) so Home processes lifetime stats / leaderboard
          // submit / unlock detection BEFORE navigating away. Then onBack
          // takes the player to the main menu (or PostRunUnlockScreen if
          // the run unlocked something).
          stopDeathEulogy();
          if (state.startTime > 0) {
            const elapsed = (Date.now() - state.startTime) / 1000;
            onRunEndRef.current?.({
              kills:        state.killCount,
              floor:        state.wave ?? 1,
              timeSurvived: elapsed,
              characterId:  state.character?.id ?? 'unknown',
              level:        state.player.level ?? 1,
              chestsOpened: state.chestsOpenedThisRun ?? 0,
              damageDealt:  getRunDamageDealt(),
            });
          }
          // onBack is a fallback in case onRunEnd doesn't navigate (e.g.
          // bot mode where onRunEnd is undefined). For normal mode,
          // onRunEnd → handleRunEnd in Home.tsx → setScreen('menu' |
          // 'post_run_unlocks'), which unmounts GameCanvas.
          onBackRef.current();
          return;
        }
        // Click missed both buttons — eat the click so it doesn't fall
        // through to in-game handlers (the world is paused on gameover
        // anyway, but be explicit).
        return;
      }

      if (state.phase === 'paused') {
        // BETA 17.30 — DPR-aware click coords (was using raw clientX/Y).
        const pPos = canvasMouse(e);
        const pX = pPos ? pPos.mx : e.clientX;
        const pY = pPos ? pPos.my : e.clientY;
        // Balance panel intercepts clicks when visible
        if (isBalancePanelVisible()) {
          handleBalancePanelClick(pX, pY, VIRTUAL_W, VIRTUAL_H);
          return;
        }
        handlePauseClick(state, pX, pY, VIRTUAL_W, VIRTUAL_H);
        return;
      }

      // Helper: check if click hit a skip/reroll button and handle it
      function handleSkipRerollClick(phase: 'levelup' | 'shrine_choice' | 'chest_choice', mx: number, my: number, btnY: number, canvasW: number, gs: NonNullable<typeof state>): boolean {
        const r = getSkipRerollButtonRects(canvasW, btnY);
        // SKIP
        if (mx >= r.skipX && mx <= r.skipX + r.skipW && my >= r.skipY && my <= r.skipY + r.skipH) {
          if (phase === 'shrine_choice') skipShrineChoice();
          if (phase === 'chest_choice') skipChestChoice();
          // level-up skip: just resume (no item/perc applied)
          // BETA 16.12 — end muffled duck (was resumeBgm). The level-up
          // open in engine.ts now calls startMuffledDuck instead of pauseBgm.
          // shrine_choice + chest_choice end-muffle inside their skip*() functions.
          if (phase === 'levelup') endMuffledDuck();
          gs.phase = 'playing';
          return true;
        }
        // REROLL
        if (mx >= r.rerollX && mx <= r.rerollX + r.rerollW && my >= r.rerollY && my <= r.rerollY + r.rerollH) {
          if (gs.rerollsLeft <= 0) return true; // disabled, eat click
          gs.rerollsLeft--;
          if (phase === 'levelup') rerollLevelUpOptions();
          if (phase === 'shrine_choice') rerollShrineOptions();
          if (phase === 'chest_choice') rerollChestOptions();
          if (!gs.pickupNotifications) gs.pickupNotifications = [];
          gs.pickupNotifications.push({ text: `REROLLED — ${gs.rerollsLeft} left`, color: '#6699ff', timer: 1.5 });
          return true;
        }
        return false;
      }

      // BETA 16.86 — All three click hit-tests now use the same
      // computeChoiceCardLayout helper that the render functions use,
      // so click zones ALWAYS match what's drawn (HUD-aware centering,
      // responsive cardW per screen size, unified math). Was: hardcoded
      // 340×460 / 300×280 + h*0.29 cardY → click zones drifted off the
      // rendered cards on narrow screens with HUD visible.

      // Click to select level-up card
      if (state.phase === 'levelup') {
        const pos = canvasMouse(e);
        if (!pos) return;
        const opts = getLevelUpOptionDisplayData();
        if (opts.length === 0) return;
        const L = computeChoiceCardLayout(VIRTUAL_W, VIRTUAL_H, opts.length, !!state.hudHidden, 'levelup');
        const btnY = L.cardY + L.cardH + 64;
        if (handleSkipRerollClick('levelup', pos.mx, pos.my, btnY, VIRTUAL_W, state)) return;
        let i = -1;
        for (let k = 0; k < opts.length; k++) {
          const cx = L.startX + k * (L.cardW + L.gap);
          if (pos.mx >= cx && pos.mx <= cx + L.cardW && pos.my >= L.cardY && pos.my <= L.cardY + L.cardH) { i = k; break; }
        }
        if (i >= 0 && !isLevelUpExitActive()) {
          playClick(); // BETA 19.34 — feedback on pick
          setLevelUpSelectedIndex(i);
          const chosen = opts[i];
          const success = handleLevelUpSelection(i);
          if (success) {
            // BETA 22.87 — exit beat instead of instant resume (see engine).
            beginLevelUpExit(state, i, opts);
            logEvent('level_up', { choice: chosen.percName, rarity: chosen.rarity, playerLevel: state.player.level });
          }
        }
        return;
      }

      // Click to select shrine choice card (horizontal layout)
      if (state.phase === 'shrine_choice') {
        const pos = canvasMouse(e);
        if (!pos) return;
        const options = getShrineBuffDisplayOptions();
        const n = options.length;
        if (n === 0) return;
        const L = computeChoiceCardLayout(VIRTUAL_W, VIRTUAL_H, n, !!state.hudHidden, 'shrine');
        const btnY = L.cardY + L.cardH + 64;
        if (handleSkipRerollClick('shrine_choice', pos.mx, pos.my, btnY, VIRTUAL_W, state)) return;
        let found = -1;
        for (let k = 0; k < n; k++) {
          const cx = L.startX + k * (L.cardW + L.gap);
          if (pos.mx >= cx && pos.mx <= cx + L.cardW && pos.my >= L.cardY && pos.my <= L.cardY + L.cardH) { found = k; break; }
        }
        if (found >= 0) {
          playClick(); // BETA 19.34 — feedback on pick
          setShrineSelectedIndex(found);
          const result = handleShrineSelection(found);
          if (result) {
            state.phase = 'playing';
            if (!state.pickupNotifications) state.pickupNotifications = [];
            state.pickupNotifications.push({ text: `${result.percName}`, color: '#00ffcc', timer: 2.5 });
            logEvent('shrine_activated', { perk: result.percName });
          }
        }
        return;
      }

      // Click to select chest item
      if (state.phase === 'chest_choice') {
        const pos = canvasMouse(e);
        if (!pos) return;
        const items = getChestOptionDisplayData();
        if (items.length === 0) return;
        const L = computeChoiceCardLayout(VIRTUAL_W, VIRTUAL_H, items.length, !!state.hudHidden, 'chest');
        const btnY = L.cardY + L.cardH + 64;
        if (handleSkipRerollClick('chest_choice', pos.mx, pos.my, btnY, VIRTUAL_W, state)) return;
        let i = -1;
        for (let k = 0; k < items.length; k++) {
          const cx = L.startX + k * (L.cardW + L.gap);
          if (pos.mx >= cx && pos.mx <= cx + L.cardW && pos.my >= L.cardY && pos.my <= L.cardY + L.cardH) { i = k; break; }
        }
        if (i >= 0) {
          playClick(); // BETA 19.34 — feedback on pick
          const result = handleChestItemSelection(i);
          if (result) {
            state.phase = 'playing';
            if (!state.pickupNotifications) state.pickupNotifications = [];
            state.pickupNotifications.push({ text: `ITEM: ${result.itemName}`, color: '#ffd700', timer: 3.0 });
            logEvent('chest_item', { item: result.itemName });
          }
        }
        return;
      }

      // BETA 20.64 — Click to pick an ultra item. The screen was keyboard-only
      // ('1'/'2'/'e') — no mouse, no gamepad — a soft-lock on handhelds.
      if (state.phase === 'ultra_chest') {
        const pos = canvasMouse(e);
        if (!pos) return;
        const rolled = getUltraChestRolled();
        if (rolled.length === 0) { closeUltraChest(state); return; }
        const L = getUltraChestLayout(VIRTUAL_W, VIRTUAL_H, rolled.length);
        for (let k = 0; k < rolled.length; k++) {
          const cx = L.startX + k * (L.cardW + L.gap);
          if (pos.mx >= cx && pos.mx <= cx + L.cardW && pos.my >= L.cardY && pos.my <= L.cardY + L.cardH) {
            playClick();
            pickUltraChestCard(state, k);
            return;
          }
        }
        return;
      }
    }

    // BETA 21.17 — clear the right-click dash token on release so each press =
    // one dash (the engine rising-edge needs 'dash' to go false between clicks).
    function onMouseUp(e: MouseEvent) {
      if (e.button === 2) stateRef.current?.keys.delete('dash');
    }

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    // BETA 23.18 — PHANTOM WALK FIX (Dylan: "my character will randomly
    // start walking N and i have to click my walking buttons to stop").
    // A keyup can be lost when focus leaves mid-hold (app switch, overlay,
    // screenshot chord) — the key stays in state.keys forever and Greg
    // auto-walks. Losing focus/visibility now clears all movement keys.
    function clearMovementKeys() {
      const st = stateRef.current;
      if (!st) return;
      for (const k of ['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright', 'dash']) st.keys.delete(k);
    }
    window.addEventListener('blur', clearMovementKeys);
    document.addEventListener('visibilitychange', clearMovementKeys);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);

    // BETA 16.31 — FOV LOCKED FOR ALL PLAYERS (incl. devs).
    // Dylan: "lock the FOV for devs too. for now. FOV should be locked for
    // all players." Wheel-zoom previously fired for DEV_USERS only; now the
    // listener is a no-op stub. The 1.2× base zoom is the only valid FOV.
    // Re-enable later by restoring the isDevUser-gated zoom logic if Dylan
    // wants devs to have it back for testing.
    function onWheel(_e: WheelEvent) {
      // Intentionally empty — FOV locked.
    }
    window.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', clearMovementKeys);
      document.removeEventListener('visibilitychange', clearMovementKeys);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('blur', onWindowBlur); // BETA 20.74
    };
  }, []);

  // Zoom locked for regular players; beta testers can scroll to zoom

  if (loading) {
    // BETA 20.74 — Dylan: "i dont like that green box at all. remove it."
    // The old lime GENERATING DUNGEON panel is gone. Loading is now a pure
    // black beat (with a barely-there pulse so slow first-loads don't read
    // as frozen) — and the real transition is the cinematic camera intro
    // that plays the moment the run starts: wide map reveal gliding into
    // your character (engine.ts _introZoomT).
    return (
      <div className="fixed inset-0 bg-black z-50">
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 text-white/15 font-mono text-xs tracking-[0.5em] animate-pulse select-none">
          ENTERING THE ZONE
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="fixed inset-0 bg-[#0a0a0a] flex flex-col items-center justify-center z-50">
        <div className="text-red-500 font-mono text-xl mb-4">ERROR</div>
        <div className="text-[#ccc] font-mono text-sm mb-6">{loadError}</div>
        <button
          onClick={onBack}
          className="px-6 py-2 border border-[#39ff14] text-[#39ff14] font-mono hover:bg-[#39ff14]/10 transition-colors"
        >
          BACK TO MENU
        </button>
      </div>
    );
  }

  return (
    // BETA 22.88 — ENFORCED 16:9 LETTERBOX (Dylan-approved). The game always
    // renders a 16:9 stage: the largest 16:9 rect that fits the window,
    // centered, with black bars filling the rest. Mouse/touch mapping is
    // untouched — canvasMouse() is getBoundingClientRect-relative, so the
    // letterboxed rect maps correctly for free.
    <div style={{ position: 'fixed', inset: 0, width: '100vw', height: '100dvh', background: '#000' }}>
      <canvas
        ref={canvasRef}
        className="block"
        style={{
          position: 'fixed',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(100vw, calc(100dvh * 16 / 9))',
          height: 'min(100dvh, calc(100vw * 9 / 16))',
          imageRendering: 'auto',
        }}
        tabIndex={0}
        // BETA 21.11 — Dylan: "you can't right click without the settings thing
        // popping up." That popup is the BROWSER's native right-click menu —
        // the game never suppressed it over the canvas, and nothing in-game
        // maps the right mouse button to pause/settings. Swallow the contextmenu
        // event so right-clicking during gameplay does nothing.
        onContextMenu={(e) => e.preventDefault()}
      />
      {/* BETA 13.47: live FPS readout. Always visible, top-right. Color-coded:
          ≥60 = green (smooth), 30-59 = yellow (mild stutter), <30 = red (lag spike).
          Below 60 the box gets a subtle red glow + pulses to "ping" you. */}
      {/* BETA 16.70 — FPS readout hidden on touch devices. Per Dylan
          (iPhone): "there should be zero dev settings showing when on
          the iphone." FPS is a perf-debug HUD element that doesn't help
          phone players — they have no way to act on it. Last remaining
          dev-style element is now suppressed. Desktop keeps it. */}
      {!isTouchDevice() && (
        <div
          style={{
            position: 'fixed',
            // BETA 16.65 — push below iPhone notch / URL bar via safe-area-inset.
            top: 'max(12px, calc(env(safe-area-inset-top) + 8px))',
            right: 'max(12px, calc(env(safe-area-inset-right) + 8px))',
            zIndex: 200,
            background: 'rgba(0,0,0,0.78)',
            border: `1px solid ${fps >= 60 ? '#1aff5e' : fps >= 30 ? '#ffd24a' : '#ff3b3b'}`,
            color:      fps >= 60 ? '#1aff5e' : fps >= 30 ? '#ffd24a' : '#ff3b3b',
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            fontSize: 11,
            fontWeight: 700,
            padding: '4px 9px',
            borderRadius: 4,
            letterSpacing: '0.04em',
            minWidth: 56,
            textAlign: 'center',
            pointerEvents: 'none',
            // Pulse / glow when below 60fps so it nags you
            boxShadow: fps < 60 ? `0 0 8px ${fps >= 30 ? 'rgba(255,210,74,0.55)' : 'rgba(255,59,59,0.65)'}` : 'none',
            animation: fps < 60 ? 'fpsPulse 0.8s ease-in-out infinite' : 'none',
          }}
        >
          {fps} FPS
        </div>
      )}
      <style>{`
        @keyframes fpsPulse {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.55; }
        }
      `}</style>
      {/* BETA 16.56 — Fullscreen toggle. Sits below FPS readout in
          top-right. Hidden in botMode to avoid clutter. F11 also works.
          BETA 16.65 — Hidden when document.documentElement.requestFullscreen
          is missing (iPhone Safari) or on touch devices generally — the
          mobile fullscreen story is "Add to Home Screen" via the hint
          banner, NOT a button that does nothing on iPhone. */}
      {!botModeRef.current && !isTouchDevice() && typeof document.documentElement.requestFullscreen === 'function' && (
        <button
          onClick={toggleFullscreen}
          title={isFs ? 'Exit fullscreen (F11)' : 'Enter fullscreen (F11)'}
          style={{
            position: 'fixed',
            top: 44,
            right: 12,
            zIndex: 200,
            background: 'rgba(0,0,0,0.78)',
            border: '1px solid rgba(255,255,255,0.35)',
            borderRadius: 4,
            color: 'rgba(255,255,255,0.85)',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            fontSize: 14,
            fontWeight: 700,
            padding: '4px 8px',
            minWidth: 30,
            minHeight: 28,
            cursor: 'pointer',
            letterSpacing: '0.04em',
            lineHeight: 1,
          }}
        >
          {isFs ? '⛶' : '⛶'/* same glyph; tooltip explains state */}
        </button>
      )}
      {/* Bot toggle button — only shown when NOT launched in botMode (avoid confusion).
          BETA 16.65 — Also hidden on touch devices. The bot toggle is
          a dev / playtester tool; on iPhone it just clutters the HUD
          fighting with the FPS readout, fullscreen button, and pause
          button for top-right real estate. Players on phones don't
          need it. Bot mode is still reachable via the menu Bot button. */}
      {!botModeRef.current && !isTouchDevice() && (
        <button
          onClick={() => {
            const enabled = toggleBot();
            setBotActive(enabled);
            botActiveRef.current = enabled;
            setBotV2Active(enabled); // keep data-collector in sync
            // BETA 13.63: honor persisted timescale on bot toggle
            if (enabled) {
              const persisted = (() => { try { const v = parseFloat(localStorage.getItem('goopzone_time_scale') || '1'); return Number.isFinite(v) && v > 0 ? v : 1; } catch { return 8; } })();
              setDebugTimeScale(persisted);
            } else {
              setDebugTimeScale(1);
            }
            // BETA 13.64: all 4 nav overlays on bot toggle
            setDebugNavOverlay(enabled);
            setDebugShowWaypoints(enabled);
            setDebugFlowField(enabled);
          }}
          style={{
            position: 'fixed',
            top: 12,
            right: 12,
            zIndex: 100,
            background: botActive ? 'rgba(0,20,0,0.88)' : 'rgba(10,10,20,0.88)',
            border: `2px solid ${botActive ? '#00ff66' : '#555'}`,
            borderRadius: 6,
            color: botActive ? '#00ff66' : '#888',
            fontFamily: 'monospace',
            fontSize: 12,
            fontWeight: 'bold',
            padding: '6px 14px',
            cursor: 'pointer',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            boxShadow: botActive ? '0 0 10px rgba(0,255,100,0.3)' : 'none',
            transition: 'all 0.15s ease',
          }}
        >
          {botActive ? '🤖 HUMAN TAKE OVER' : '🤖 BOT TAKE OVER'}
        </button>
      )}
      {debugOpen && <DebugPanel stateRef={stateRef} />}
      {/* BETA 16.57 — Mobile touch controls. Only rendered when device
          is touch-capable (or when forceShow flag is on, future Settings
          toggle). Hidden during bot mode. */}
      {!botModeRef.current && (
        <TouchControls
          onKey={handleTouchKey}
          onPause={handleTouchPause}
        />
      )}
    </div>
  );
}
